import React, { useState, useEffect, useCallback } from 'react';

import {
  Search, Edit, X, XCircle, FileText, DollarSign, Clock,
  Package, Building2, RefreshCw, Check, Loader2, Save,

  ChevronDown, ChevronRight as ChevronRightIcon, Send, AlertTriangle,

  Globe, ShoppingCart, AlertCircle, Plus, ClipboardList, Calendar, Eye,

  Mail, FileCheck, CheckCircle, Info

} from 'lucide-react';

import api from '../../../services/api';
import { cotizacionesService, comprasPendientesService, proveedoresService } from '../../../services/api';
import { ProveedorModal } from '../Proveedores/ProveedoresView';
import useStockValidation from '../../../hooks/useStockValidation';
import useModal from '../../../hooks/useModal';
import stockVerificacionService from '../../../services/stockVerificacionService';
import FacturaPrintModal from '../Factura/FacturaPrintModal';
import cotizacionTemplateHtml from '../Factura/cotizacionTemplate.js';
import FacturaEmisionModal from '../Factura/FacturaEmisionModal';
import { useApp } from '../../../context/AppContext';




// ==================== SERVICIO DE SOLICITUDES ====================

const solicitudesService = {

  // Obtener todas las solicitudes

  getAll: async (params = {}) => {

    try {

      const response = await api.get('/solicitudes', { params });

      return response.data;

    } catch (error) {

      throw error.response?.data || { message: 'Error al obtener solicitudes' };

    }

  }

};



// ==================== CONFIGURACIÓN DE ESTADOS ====================

const ESTADOS_CONFIG = {

  borrador: { label: 'Borrador', color: 'bg-slate-100 text-slate-700 border-slate-300', icon: FileText },

  en_proceso: { label: 'En Proceso', color: 'bg-yellow-100 text-yellow-800 border-yellow-300', icon: Clock },

  lista_confirmar: { label: 'Lista para Confirmar', color: 'bg-brand-100 text-brand-800 border-brand-300', icon: Send },

  confirmada: { label: 'Confirmada', color: 'bg-green-100 text-green-800 border-green-300', icon: Check },

  en_espera_pago: { label: 'En Espera de Pago', color: 'bg-brand-100 text-brand-800 border-brand-300', icon: DollarSign },

  pagada: { label: 'Pagada', color: 'bg-emerald-100 text-emerald-800 border-emerald-300', icon: CheckCircle },

  rechazada: { label: 'Rechazada', color: 'bg-red-100 text-red-700 border-red-300', icon: XCircle }

};



// ==================== UTILIDADES ====================

const formatearMoneda = (monto) => {

  const valor = parseFloat(monto);

  if (!Number.isFinite(valor)) return '$0.00';

  return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(valor);

};



const formatearFecha = (fecha) => {

  if (!fecha) return '-';

  return new Date(fecha).toLocaleDateString('es-MX', { day: '2-digit', month: '2-digit', year: 'numeric' });

};



// Formatear artículos con valores por defecto para manejar NULL del backend

const formatearArticulos = (articulos = []) => {

  return articulos.map(art => ({

    id: art.id,

    articulo_id: art.articulo_id,

    nombre: art.nombre || art.articulo_nombre || 'Sin nombre',

    cantidad: art.cantidad || 1,

    precio_unitario: art.precio_unitario ?? 0,

    subtotal: art.subtotal ?? 0,

    // La API ya devuelve un booleano real (Postgres); === 1/=== 0 era del
    // MySQL viejo y nunca hacia match, dejando tiene_stock en null siempre
    // aunque la base tuviera true/false -- eso bloqueaba la completitud.
    tiene_stock: (art.tiene_stock === true || art.tiene_stock === 1) ? true
      : (art.tiene_stock === false || art.tiene_stock === 0) ? false
      : null,

    stock_disponible: art.stock_disponible || 0,

    link_proveedor: art.link_proveedor || '',

    observaciones: art.observaciones || '',

    tiempo_entrega: art.tiempo_entrega || '',

    articulo_tipo: art.articulo_tipo,

    articulo_codigo: art.articulo_codigo,

    articulo_marca: art.articulo_marca,

    precio_costo_sugerido: art.precio_costo_sugerido || 0,

    // Campos adicionales del formulario

    costo_envio: art.costo_envio ?? 0,

    via_compra: art.via_compra || '',

    porcentaje_utilidad: art.porcentaje_utilidad ?? 60,

    precio_venta: art.precio_venta ?? 0,

    margen_ganancia: art.margen_ganancia ?? 0,

    descripcion: art.descripcion || '',

    unidad_medida: art.unidad_medida || 'pzas'

  }));

};



// Validar artículo antes de guardar

const validarArticulo = (articulo) => {

  const errores = [];



  if (!articulo.precio_unitario || articulo.precio_unitario <= 0) {

    errores.push('El precio unitario debe ser mayor a 0');

  }

  // El stock ya no lo decide el usuario: se consulta contra el inventario
  // real (ver ArticuloEditRow). Con precio y vía de compra alcanza para
  // cotizar -- si falta stock, el envío a Compras Pendientes con la
  // cantidad exacta ocurre al confirmar la cotización, no aquí.

  if (!articulo.via_compra) {

    errores.push('Debe seleccionar una vía de compra');

  }



  return errores;

};



// eslint-disable-next-line no-unused-vars
const calcularSubtotal = (cantidad, precioUnitario) => {
  return (cantidad || 0) * (precioUnitario || 0);
};



// ==================== COMPONENTES ====================



// Chip de Estado

const EstadoChip = ({ estado, size = 'normal' }) => {

  const config = ESTADOS_CONFIG[estado] || ESTADOS_CONFIG.borrador;

  const Icon = config.icon;

  const sizeClass = size === 'small' ? 'px-2 py-0.5 text-xs gap-1' : 'px-2.5 py-1 text-sm gap-1.5';



  return (

    <span className={`inline-flex items-center ${sizeClass} rounded-full font-semibold border ${config.color}`}>

      <Icon size={size === 'small' ? 12 : 14} />

      {config.label}

    </span>

  );

};



// ==================== LISTA DE ARTÍCULOS CON EDICIÓN MÚLTIPLE ====================

const ArticulosListManager = ({ articulos, cotizacionId, onUpdate, readOnly, saving, cotizacionInfo }) => {
  // cotizacionInfo = { numero_cotizacion, cliente_nombre, empresa_nombre }
  const { notify, confirm: confirmModal, ModalRenderer } = useModal();
  const { handleViewChange, setArticuloFilterId } = useApp();

  // Al hacer click en un articulo ya cotizado (con ficha real en el
  // catalogo), navega al modulo Articulos y abre su detalle -- los
  // articulos externos (articulo_id null, creados ad-hoc en la
  // cotizacion) no tienen a donde ir.
  const handleVerArticulo = (articuloId) => {
    if (!articuloId) return;
    setArticuloFilterId(articuloId);
    handleViewChange('articulos');
  };

  const [articulosSeleccionados, setArticulosSeleccionados] = useState([]);

  const [modoEdicionMasiva, setModoEdicionMasiva] = useState(false);

  const [articuloEditandoId, setArticuloEditandoId] = useState(null);

  const [guardandoMasivo, setGuardandoMasivo] = useState(false);

  // Campos para edición masiva

  const [precioCostoGlobal, setPrecioCostoGlobal] = useState('');

  const [costoEnvioGlobal, setCostoEnvioGlobal] = useState('');

  const [viaCompraGlobal, setViaCompraGlobal] = useState('');

  const [porcentajeUtilidadGlobal, setPorcentajeUtilidadGlobal] = useState('');

  const [stockGlobal, setStockGlobal] = useState(null);



  // Resetear selección cuando cambian los artículos

  useEffect(() => {

    setArticulosSeleccionados([]);

    setModoEdicionMasiva(false);

  }, [articulos]);



  const toggleSeleccionarTodo = () => {

    if (articulosSeleccionados.length === articulos.length) {

      setArticulosSeleccionados([]);

    } else {

      setArticulosSeleccionados(articulos.map(a => a.id));

    }

  };



  const toggleSeleccionarArticulo = (id) => {

    setArticulosSeleccionados(prev => 

      prev.includes(id) 

        ? prev.filter(aid => aid !== id)

        : [...prev, id]

    );

  };



  const handleGuardarMasivo = async () => {

    if (articulosSeleccionados.length === 0) return;

    setGuardandoMasivo(true);

    try {

      // Construir payload con todos los artículos seleccionados
      const payload = [];

      // BUG FIX: articulosSeleccionados contiene IDs (números), no objetos.
      // Cruzamos cada ID con el array de artículos completo.
      for (const articuloId of articulosSeleccionados) {
        const articulo = articulos.find(a => a.id === articuloId);
        if (!articulo) continue;

        // Obtener valores del artículo original o globales
        const precioCosto = precioCostoGlobal !== '' ? parseFloat(precioCostoGlobal) : (articulo.precio_unitario || 0);
        const envio = costoEnvioGlobal !== '' ? parseFloat(costoEnvioGlobal) : (articulo.costo_envio || 0);
        const utilidadPct = porcentajeUtilidadGlobal !== '' ? parseFloat(porcentajeUtilidadGlobal) : (articulo.porcentaje_utilidad || 60);
        const viaCompra = viaCompraGlobal || articulo.via_compra || 'nacional';
        const tieneStockValor = stockGlobal !== null ? stockGlobal : articulo.tiene_stock;

        // Si no, preservar el precio_venta original del artículo

        let precioVenta;

        let margenGanancia;

        

        if (precioCostoGlobal !== '' || costoEnvioGlobal !== '') {

          // Usuario estableció costos globales: recalcular todo

          // El envío NO se suma al precio de venta, solo al final del total

          precioVenta = precioCosto * (1 + utilidadPct / 100);

          margenGanancia = precioVenta - precioCosto;

        } else {

          // No hay costos globales: mantener precio_venta original, solo actualizar utilidad

          precioVenta = articulo.precio_venta || articulo.precio_unitario || 0;

          // Recalcular margen basado en nuevo % de utilidad si se cambió

          if (porcentajeUtilidadGlobal !== '') {

            const costoUnitario = (articulo.precio_unitario || 0);  // Sin envío

            const nuevoPrecioVenta = costoUnitario * (1 + utilidadPct / 100);

            precioVenta = nuevoPrecioVenta;

            // El margen se calcula solo sobre el costo del artículo

            margenGanancia = nuevoPrecioVenta - costoUnitario;

          } else {

            // Margen sin envío

            margenGanancia = precioVenta - (articulo.precio_unitario || 0);

          }

        }

        const subtotalVenta = precioVenta * (articulo.cantidad || 1);

        // Agregar al payload bulk
        payload.push({
          id: articuloId,
          precio_unitario: precioCosto,
          costo_envio: envio,
          via_compra: viaCompra,
          porcentaje_utilidad: utilidadPct,
          precio_venta: precioVenta,
          margen_ganancia: margenGanancia,
          link_proveedor: articulo.link_proveedor || null,
          tiene_stock: tieneStockValor,
          subtotal: subtotalVenta,
          stock_disponible: articulo.stock_disponible || null,
          observaciones: articulo.observaciones || '',
          tiempo_entrega: articulo.tiempo_entrega || ''
        });

      }

      // Enviar todos los artículos en una sola llamada bulk al backend
      console.log('📤 Enviando payload bulk:', payload);
      const result = await cotizacionesService.updateArticulosBulk(cotizacionId, payload);
      console.log('✅ Respuesta del backend:', result);

      // El envío a Compras Pendientes de los artículos sin stock no se
      // dispara aquí: lo hace, con la cantidad exacta que falta, el
      // flujo de Confirmar Cotización (ver handleConfirmar).
      const successMsg = `${result.data?.articulos_actualizados || payload.length} artículo(s) actualizados correctamente.`;
      await notify('success', '¡Guardado exitoso!', successMsg);

      

      // Recargar cotización una sola vez al final para actualizar la barra de progreso

      if (onUpdate) {

        await onUpdate(cotizacionId, null, {});

      }

      

      setModoEdicionMasiva(false);

      setArticulosSeleccionados([]);

      setPrecioCostoGlobal('');

      setCostoEnvioGlobal('');

      setViaCompraGlobal('');

      setPorcentajeUtilidadGlobal('');

      setStockGlobal(null);

    } catch (error) {

      await notify('error', 'Error al guardar', error.message || 'No se pudieron guardar los cambios masivamente. Revisa la consola para más detalles.');

    } finally {

      setGuardandoMasivo(false);

    }

  };



  // Selector de proveedor para un item de cotizacion: elegir uno ya
  // registrado, o abrir el mismo formulario de registro de Proveedores
  // (con RIF, telefono, email, cuentas bancarias, etc.) sin salir del
  // flujo de cotizar. Se reusa en ambos editores de item de mas abajo.
  const SelectorProveedor = ({ proveedorId, onChange }) => {
    const [proveedores, setProveedores] = useState([]);
    const [cargando, setCargando] = useState(true);
    const [mostrarModal, setMostrarModal] = useState(false);

    const cargarProveedores = useCallback(async () => {
      try {
        const respuesta = await proveedoresService.getAll();
        setProveedores(respuesta.data || []);
      } catch (error) {
        console.error('Error al cargar proveedores:', error);
      } finally {
        setCargando(false);
      }
    }, []);

    useEffect(() => { cargarProveedores(); }, [cargarProveedores]);

    return (
      <div className="mb-5">
        <label className="block text-xs font-semibold text-slate-600 mb-2">
          🏷️ Proveedor registrado
        </label>
        <div className="flex gap-2">
          <select
            value={proveedorId || ''}
            onChange={(e) => onChange(e.target.value ? parseInt(e.target.value, 10) : null)}
            disabled={cargando}
            className="flex-1 px-3 py-2.5 text-sm border border-slate-200 rounded-xl bg-white focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 focus:outline-none"
          >
            <option value="">— Sin proveedor registrado —</option>
            {proveedores.map(p => (
              <option key={p.id} value={p.id}>{p.nombre}{p.rif ? ` (${p.rif})` : ''}</option>
            ))}
          </select>
          <button
            type="button"
            onClick={() => setMostrarModal(true)}
            className="px-3 py-2.5 text-sm rounded-xl border border-blue-300 text-blue-700 hover:bg-blue-50 whitespace-nowrap"
          >
            + Registrar
          </button>
        </div>

        {mostrarModal && (
          <ProveedorModal
            mode="create"
            proveedor={null}
            onClose={() => setMostrarModal(false)}
            onSuccess={async (_mensaje, creado) => {
              setMostrarModal(false);
              await cargarProveedores();
              if (creado?.id) onChange(creado.id);
            }}
          />
        )}
      </div>
    );
  };

  // Componente de fila en modo edición individual

  const ArticuloEditRow = ({ articulo, onCancel, onSave }) => {

    const [guardando, setGuardando] = useState(false);

    const [precioCosto, setPrecioCosto] = useState(articulo.precio_unitario?.toString() || '');

    const [costoEnvio, setCostoEnvio] = useState(articulo.costo_envio?.toString() || '0');

    const [viaCompra, setViaCompra] = useState(articulo.via_compra || 'nacional');

    const [porcentajeUtilidad, setPorcentajeUtilidad] = useState((articulo.porcentaje_utilidad ?? 60).toString());

    const [url, setUrl] = useState(articulo.link_proveedor || '');

    const [proveedorId, setProveedorId] = useState(articulo.proveedor_id || null);

    // El stock ya no lo decide el usuario a mano: se consulta contra el
    // inventario real (stock_actual - stock_reservado, con reservas de
    // otras cotizaciones ya descontadas). Solo los artículos "externos"
    // (articulo_id null, sin ficha en el catálogo) no tienen stock que
    // consultar y quedan con el toggle manual de siempre.
    const consultaAutomatica = !!articulo.articulo_id;
    const [stockInfo, setStockInfo] = useState({ checking: consultaAutomatica, disponible: null, faltante: null, suficiente: null, error: null });
    const [tieneStockManual, setTieneStockManual] = useState(articulo.tiene_stock);

    useEffect(() => {
      if (!consultaAutomatica) return;
      let cancelado = false;
      setStockInfo({ checking: true, disponible: null, faltante: null, suficiente: null, error: null });

      stockVerificacionService
        .verificarStockCotizacion(cotizacionId, [{ articulo_id: articulo.articulo_id, cantidad: articulo.cantidad }])
        .then((resultado) => {
          if (cancelado) return;
          const faltante = resultado.articulosFaltantes?.[0];
          const ok = resultado.articulosOK?.[0];
          const item = faltante || ok;
          setStockInfo({
            checking: false,
            disponible: item ? item.stock_disponible : null,
            faltante: faltante ? faltante.cantidad_faltante : 0,
            suficiente: item ? !faltante : null,
            error: null
          });
        })
        .catch((err) => {
          if (cancelado) return;
          setStockInfo({ checking: false, disponible: null, faltante: null, suficiente: null, error: err.message || 'No se pudo consultar el stock' });
        });

      return () => { cancelado = true; };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [articulo.articulo_id, articulo.cantidad, cotizacionId]);

    // Si falló la consulta, no dejamos al usuario bloqueado: cae al
    // toggle manual como respaldo.
    const usaStockManual = !consultaAutomatica || !!stockInfo.error;
    const tieneStock = usaStockManual ? tieneStockManual : stockInfo.suficiente;



    // Cálculos en tiempo real

    const costoBase = parseFloat(precioCosto) || 0;

    const envio = parseFloat(costoEnvio) || 0;

    const utilidadPct = parseFloat(porcentajeUtilidad) || 0;

    // El envío NO se suma al precio de venta, solo al final del total

    const precioVenta = costoBase * (1 + utilidadPct / 100);

    const margenGanancia = precioVenta - costoBase;

    const subtotalVenta = precioVenta * (articulo.cantidad || 1);



    const handleGuardar = async () => {

      const errores = validarArticulo({

        precio_unitario: costoBase,

        tiene_stock: tieneStock,

        link_proveedor: url.trim(),

        proveedor_id: proveedorId,

        via_compra: viaCompra

      });



      if (errores.length > 0) {

        await notify('warning', 'Datos incompletos', errores.join('\n'));

        return;

      }



      setGuardando(true);

      try {

        await onSave(cotizacionId, articulo.id, {

          precio_unitario: costoBase,

          costo_envio: envio,

          via_compra: viaCompra,

          porcentaje_utilidad: utilidadPct,

          precio_venta: precioVenta,

          margen_ganancia: margenGanancia,

          link_proveedor: url.trim() || null,

          proveedor_id: proveedorId,

          tiene_stock: tieneStock,

          subtotal: subtotalVenta

        });

        // El envío a Compras Pendientes ya no se dispara aquí: pasarlo
        // por artículo mandaba la cantidad completa en vez de la que
        // realmente falta y duplicaba lo que ya hace, con el dato exacto,
        // el flujo de Confirmar Cotización (ver handleConfirmar).

        setArticuloEditandoId(null);

      } catch (error) {

        await notify('error', 'Error al guardar', error.message || 'No se pudo guardar el artículo.');

      } finally {

        setGuardando(false);

      }

    };



    return (

<div className="col-span-full bg-gradient-to-r from-brand-50 to-brand-50 border border-brand-200/80 rounded-2xl p-5 shadow-sm mt-2">
  {/* Header */}
  <div className="flex items-center gap-2.5 mb-4 pb-3 border-b border-brand-200/80">
    <div className="p-1.5 bg-brand-100 rounded-lg">
      <Edit size={18} className="text-brand-600" />
    </div>
    <h5 className="font-semibold text-brand-900 text-base">Editando: {articulo.nombre}</h5>
    <span className="text-sm text-brand-600 font-medium">({articulo.cantidad} {articulo.unidad_medida || 'pzas'})</span>
  </div>

  {/* Sección Precios y Costos */}
  <div className="mb-5">
    <p className="text-xs font-semibold text-brand-700 uppercase tracking-wider mb-3">💰 Precios y Costos</p>
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
      {/* Costo Unitario */}
      <div className="bg-white/70 rounded-xl p-3 border border-brand-100/80 shadow-sm">
        <label className="block text-xs font-semibold text-slate-600 mb-1.5">
          💰 Costo Unitario
        </label>
        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm font-medium">$</span>
          <input
            type="number"
            step="0.01"
            min="0"
            value={precioCosto}
            onChange={(e) => setPrecioCosto(e.target.value)}
            className="w-full pl-8 pr-3 py-2.5 text-sm border border-slate-200 rounded-xl focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 focus:outline-none bg-white transition-all shadow-sm"
            placeholder="0.00"
          />
        </div>
      </div>

      {/* Costo de Envío */}
      <div className="bg-white/70 rounded-xl p-3 border border-brand-100/80 shadow-sm">
        <label className="block text-xs font-semibold text-slate-600 mb-1.5">
          🚚 Costo Envío
        </label>
        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm font-medium">$</span>
          <input
            type="number"
            step="0.01"
            min="0"
            value={costoEnvio}
            onChange={(e) => setCostoEnvio(e.target.value)}
            className="w-full pl-8 pr-3 py-2.5 text-sm border border-slate-200 rounded-xl focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 focus:outline-none bg-white transition-all shadow-sm"
            placeholder="0.00"
          />
        </div>
      </div>

      {/* % Utilidad */}
      <div className="bg-white/70 rounded-xl p-3 border border-brand-100/80 shadow-sm">
        <label className="block text-xs font-semibold text-slate-600 mb-1.5">
          📈 % Utilidad
        </label>
        <div className="relative">
          <input
            type="number"
            step="1"
            min="0"
            max="100"
            value={porcentajeUtilidad}
            onChange={(e) => setPorcentajeUtilidad(e.target.value)}
            className="w-full pl-3 pr-8 py-2.5 text-sm border border-slate-200 rounded-xl focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 focus:outline-none bg-white transition-all shadow-sm"
            placeholder="60"
          />
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm font-medium">%</span>
        </div>
      </div>
    </div>
  </div>

  {/* Precio Venta Calculado */}
  <div className="mb-5">
    <p className="text-xs font-semibold text-brand-700 uppercase tracking-wider mb-2">💵 Precio de Venta Calculado</p>
    <div className="bg-white rounded-xl p-4 border border-emerald-200/80 shadow-sm">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs text-emerald-600 font-semibold">P. Venta x {articulo.cantidad || 1} pzas</p>
          <p className="text-xl font-bold text-emerald-700 tabular-nums">{formatearMoneda(precioVenta)} <span className="text-sm font-normal text-emerald-600">c/u</span></p>
        </div>
        <div className="text-right">
          <p className="text-xs text-slate-500 font-medium">Margen/unidad</p>
          <p className="text-sm font-bold text-emerald-600 tabular-nums">{formatearMoneda(margenGanancia)}</p>
        </div>
      </div>
    </div>
  </div>

  {/* Sección Opciones */}
  <div className="mb-5">
    <p className="text-xs font-semibold text-brand-700 uppercase tracking-wider mb-3">🌍 Opciones</p>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      {/* Vía de Compra */}
      <div className="bg-white/70 rounded-xl p-3 border border-brand-100/80 shadow-sm">
        <label className="block text-xs font-semibold text-slate-600 mb-2">🌍 Vía de Compra</label>
        <div className="flex gap-1.5">
          <button
            type="button"
            onClick={() => setViaCompra('nacional')}
            className={`flex-1 py-2.5 px-2 rounded-xl border-2 text-xs font-semibold transition-all duration-200 ${
              viaCompra === 'nacional'
                ? 'bg-emerald-50 border-emerald-500 text-emerald-700 shadow-sm'
                : 'border-slate-200 hover:bg-slate-50 bg-white text-slate-600'
            }`}
          >
            🏠 Nacional
          </button>
          <button
            type="button"
            onClick={() => setViaCompra('internacional')}
            className={`flex-1 py-2.5 px-2 rounded-xl border-2 text-xs font-semibold transition-all duration-200 ${
              viaCompra === 'internacional'
                ? 'bg-brand-50 border-brand-500 text-brand-700 shadow-sm'
                : 'border-slate-200 hover:bg-slate-50 bg-white text-slate-600'
            }`}
          >
            🌍 Internacional
          </button>
        </div>
      </div>

      {/* Stock */}
      <div className="bg-white/70 rounded-xl p-3 border border-blue-100/80 shadow-sm">
        <label className="block text-xs font-semibold text-slate-600 mb-2">📦 Stock</label>
        {!consultaAutomatica ? (
          <div className="flex gap-1.5">
            <button
              type="button"
              onClick={() => setTieneStockManual(true)}
              className={`flex-1 py-2.5 px-2 rounded-xl border-2 text-xs font-semibold transition-all duration-200 ${
                tieneStockManual === true
                  ? 'bg-emerald-50 border-emerald-500 text-emerald-700 shadow-sm'
                  : 'border-slate-200 hover:bg-slate-50 bg-white text-slate-600'
              }`}
            >
              ✓ Disponible
            </button>
            <button
              type="button"
              onClick={() => setTieneStockManual(false)}
              className={`flex-1 py-2.5 px-2 rounded-xl border-2 text-xs font-semibold transition-all duration-200 ${
                tieneStockManual === false
                  ? 'bg-red-50 border-red-500 text-red-700 shadow-sm'
                  : 'border-slate-200 hover:bg-slate-50 bg-white text-slate-600'
              }`}
            >
              ✗ Sin stock
            </button>
          </div>
        ) : stockInfo.checking ? (
          <p className="text-xs text-slate-500 flex items-center gap-1.5 py-2.5">
            <Loader2 size={14} className="animate-spin" /> Consultando inventario...
          </p>
        ) : stockInfo.error ? (
          <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-2">
            No se pudo consultar el inventario. Indícalo a mano:
            <div className="flex gap-1.5 mt-1.5">
              <button type="button" onClick={() => setTieneStockManual(true)}
                className={`flex-1 py-2 px-2 rounded-lg border-2 text-xs font-semibold ${tieneStockManual === true ? 'bg-emerald-50 border-emerald-500 text-emerald-700' : 'border-slate-200 bg-white text-slate-600'}`}>
                ✓ Disponible
              </button>
              <button type="button" onClick={() => setTieneStockManual(false)}
                className={`flex-1 py-2 px-2 rounded-lg border-2 text-xs font-semibold ${tieneStockManual === false ? 'bg-red-50 border-red-500 text-red-700' : 'border-slate-200 bg-white text-slate-600'}`}>
                ✗ Sin stock
              </button>
            </div>
          </div>
        ) : tieneStock ? (
          <p className="text-sm font-semibold text-emerald-700 py-2.5">✓ Disponible ({stockInfo.disponible} en almacén)</p>
        ) : (
          <p className="text-sm font-semibold text-amber-700 py-2.5">
            ⚠ Faltan {stockInfo.faltante} de {articulo.cantidad} — se cotiza igual, y al confirmar se manda a Compras Pendientes
          </p>
        )}
      </div>
    </div>
  </div>

  <SelectorProveedor proveedorId={proveedorId} onChange={setProveedorId} />

  {/* URL Proveedor */}
  <div className="mb-5">
    <label className="block text-xs font-semibold text-slate-600 mb-2">
      🔗 URL Proveedor {tieneStock === false && <span className="text-slate-400 font-normal">(opcional, ayuda a Compras)</span>}
    </label>
    <div className="relative">
      <Globe size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
      <input
        type="url"
        value={url}
        onChange={(e) => setUrl(e.target.value)}
        className="w-full pl-10 pr-3 py-2.5 text-sm border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 focus:outline-none bg-white transition-all shadow-sm"
        placeholder="https://proveedor.com/producto (opcional si ya elegiste un proveedor registrado arriba)"
      />
    </div>
  </div>

  {/* Botones */}
  <div className="flex gap-3 pt-4 border-t border-brand-200/80">
    <button
      onClick={onCancel}
      className="px-5 py-2.5 border border-slate-200 rounded-xl hover:bg-slate-50 font-semibold text-sm text-slate-600 transition-all duration-200 active:scale-95"
      disabled={guardando}
    >
      Cancelar
    </button>
    <button
      onClick={handleGuardar}
      disabled={guardando || saving || stockInfo.checking}
      className="flex-1 py-2.5 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 font-semibold text-sm flex items-center justify-center gap-2 disabled:bg-slate-300 disabled:cursor-not-allowed transition-all duration-200 shadow-sm active:scale-95"
    >
      {guardando ? (
        <><Loader2 size={16} className="animate-spin" /> Guardando...</>
      ) : (
        <><Save size={16} /> Guardar Cambios</>
      )}
    </button>
  </div>
</div>

    );

  };



  // Componente de fila en modo vista

const ArticuloViewRow = ({ articulo, onEdit, onVerArticulo, isSelected, onToggleSelect, disableIndividualEdit }) => {
  const tienePrecio = (articulo.precio_unitario || 0) > 0;
  // El stock ya no es un dato que el usuario deba completar (se consulta
  // contra el inventario real) -- con precio y vía de compra ya se puede
  // cotizar el artículo, tenga stock o no.
  const completo = tienePrecio && !!articulo.via_compra;

  return (
    <>
      <div className={`grid grid-cols-12 gap-2 p-3.5 items-center ${completo ? 'bg-emerald-50/40' : 'bg-amber-50/40'} hover:bg-slate-50/80 transition-colors duration-200`}>
        {/* Checkbox */}
        <div className="col-span-1 flex items-center justify-center">
          {!readOnly && (
            <input
              type="checkbox"
              checked={isSelected}
              onChange={() => onToggleSelect(articulo.id)}
              className="w-4 h-4 text-brand-600 rounded border-slate-300 focus:ring-brand-500/20 cursor-pointer transition-all"
            />
          )}
        </div>

        {/* Nombre */}
        <div className="col-span-3">
          <p
            className={`font-semibold text-slate-800 text-sm truncate ${articulo.articulo_id ? 'cursor-pointer hover:text-brand-600 hover:underline' : ''}`}
            title={articulo.articulo_id ? `Ver ficha de ${articulo.nombre}` : articulo.nombre}
            onClick={articulo.articulo_id ? onVerArticulo : undefined}
          >
            {articulo.nombre}
          </p>
          <p className="text-xs text-slate-500 mt-0.5">
            {articulo.cantidad} {articulo.unidad_medida || 'pzas'}
          </p>
        </div>

        {/* Costo Unit. (columna vecina "P. Venta" ya muestra el precio de venta) */}
        <div className="col-span-1 text-center">
          <p className="text-xs text-slate-500 font-medium">Costo Unit.</p>
          <p className={`text-sm font-bold tabular-nums ${tienePrecio ? 'text-slate-700' : 'text-red-500'}`}>
            {tienePrecio ? formatearMoneda(articulo.precio_unitario) : 'Sin costo'}
          </p>
        </div>

        {/* Envío */}
        <div className="col-span-1 text-center">
          <p className="text-xs text-slate-500 font-medium">Envío</p>
          <p className="text-sm font-bold text-amber-600 tabular-nums">
            {formatearMoneda(articulo.costo_envio || 0)}
          </p>
        </div>

        {/* Utilidad */}
        <div className="col-span-1 text-center">
          <p className="text-xs text-slate-500 font-medium">Util.</p>
          <p className="text-sm font-bold text-brand-600 tabular-nums">
            {(articulo.porcentaje_utilidad ?? 60)}%
          </p>
        </div>

        {/* Precio Venta */}
        <div className="col-span-2 text-center">
          <p className="text-xs text-slate-500 font-medium">P. Venta</p>
          <p className="text-sm font-bold text-brand-700 tabular-nums">
            {formatearMoneda(articulo.precio_venta || 0)}
          </p>
        </div>

        {/* Stock */}
        <div className="col-span-1 text-center">
          <p className="text-xs text-slate-500 font-medium">Stock</p>
          <p className={`text-sm font-bold ${
            articulo.tiene_stock === true ? 'text-emerald-700' :
            articulo.tiene_stock === false ? 'text-red-600' : 'text-slate-400'
          }`}>
            {articulo.tiene_stock === true ? '✓' :
            articulo.tiene_stock === false ? '✗' : '?'}
          </p>
        </div>

        {/* Vía */}
        <div className="col-span-1 text-center">
          <p className="text-xs text-slate-500 font-medium">Vía</p>
          <p className="text-sm">
            {articulo.via_compra === 'internacional' ? (
              <span className="text-brand-600" title="Internacional">🌍</span>
            ) : articulo.via_compra === 'nacional' ? (
              <span className="text-emerald-600" title="Nacional">🏠</span>
            ) : (
              <span className="text-slate-400">—</span>
            )}
          </p>
        </div>

        {/* Acciones */}
        <div className="col-span-1 text-right">
          {!readOnly && (
            <button
              onClick={onEdit}
              disabled={saving || disableIndividualEdit}
              className={`p-2 text-white rounded-xl text-xs font-semibold flex items-center gap-1 transition-all duration-200 active:scale-95 ${
                disableIndividualEdit 
                  ? 'bg-slate-300 cursor-not-allowed' 
                  : 'bg-brand-600 hover:bg-brand-700 shadow-sm'
              }`}
              title={disableIndividualEdit ? 'Deshabilitado durante edición múltiple' : 'Editar artículo'}
            >
              <Edit size={12} />
            </button>
          )}
        </div>
      </div>
    </>
  );
};



  if (!articulos || articulos.length === 0) {

    return (

      <div className="text-center py-8 text-slate-500 bg-slate-50 rounded-xl border-2 border-dashed">

        <Package size={40} className="mx-auto mb-2 text-slate-300" />

        <p>No hay artículos en esta cotización</p>

      </div>

    );

  }



  return (
    <>
    {ModalRenderer}
    <div className="space-y-3">

      {/* Panel de Edición Masiva - Solo aparece con 2+ artículos */}

      {modoEdicionMasiva && articulosSeleccionados.length >= 2 && (

        <div className="bg-gradient-to-r from-brand-50 to-brand-50 border-2 border-brand-200 rounded-xl p-4 shadow-md">

          {/* Header */}

          <div className="flex items-center gap-2 mb-4 pb-3 border-b border-brand-200">

            <div className="p-1.5 bg-brand-100 rounded-lg">

              <Edit size={18} className="text-brand-600" />

            </div>

            <h4 className="font-semibold text-brand-800 text-base">

              Editar {articulosSeleccionados.length} artículos

            </h4>

          </div>



          {/* Sección de Campos Numéricos */}

          <div className="mb-5">

            <p className="text-xs font-medium text-brand-600 uppercase tracking-wide mb-3">💰 Precios y Costos</p>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">

              {/* Costo Unitario Global */}

              <div className="bg-white/60 rounded-lg p-3 border border-brand-100">

                <label className="block text-xs font-medium text-slate-700 mb-1.5">

                  💰 Costo Unitario

                </label>

                <div className="relative">

                  <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500 text-sm">$</span>

                  <input

                    type="number"

                    step="0.01"

                    min="0"

                    value={precioCostoGlobal}

                    onChange={(e) => setPrecioCostoGlobal(e.target.value)}

                    className="w-full pl-7 pr-2 py-2 text-sm border-2 border-slate-200 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 bg-white"

                    placeholder="0.00"

                  />

                </div>

              </div>



              {/* Costo de Envío Global */}

              <div className="bg-white/60 rounded-lg p-3 border border-brand-100">

                <label className="block text-xs font-medium text-slate-700 mb-1.5">

                  🚚 Costo Envío

                </label>

                <div className="relative">

                  <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500 text-sm">$</span>

                  <input

                    type="number"

                    step="0.01"

                    min="0"

                    value={costoEnvioGlobal}

                    onChange={(e) => setCostoEnvioGlobal(e.target.value)}

                    className="w-full pl-7 pr-2 py-2 text-sm border-2 border-slate-200 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 bg-white"

                    placeholder="0.00"

                  />

                </div>

              </div>



              {/* % Utilidad Global */}

              <div className="bg-white/60 rounded-lg p-3 border border-brand-100">

                <div className="flex items-center justify-between mb-1.5">

                  <label className="text-xs font-medium text-slate-700">

                    📈 % Utilidad

                  </label>

                  {porcentajeUtilidadGlobal !== '' && (

                    <span className="text-xs font-bold text-brand-600 bg-brand-100 px-2 py-0.5 rounded">

                      {porcentajeUtilidadGlobal}%

                    </span>

                  )}

                </div>

                <div className="relative mb-2">

                  <input

                    type="number"

                    step="1"

                    min="0"

                    max="100"

                    value={porcentajeUtilidadGlobal}

                    onChange={(e) => setPorcentajeUtilidadGlobal(e.target.value)}

                    className="w-full pl-2 pr-7 py-2 text-sm border-2 border-slate-200 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 bg-white"

                    placeholder="60"

                  />

                  <span className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 text-sm">%</span>

                </div>

                {/* Barra de progreso visual 0-100% */}

                <div className="space-y-1">

                  <input

                    type="range"

                    min="0"

                    max="100"

                    value={porcentajeUtilidadGlobal === '' ? 0 : Number(porcentajeUtilidadGlobal)}

                    onChange={(e) => setPorcentajeUtilidadGlobal(e.target.value)}

                    className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-brand-600"

                  />

                  <div className="flex justify-between text-xs text-slate-400">

                    <span>0%</span>

                    <span>25%</span>

                    <span>50%</span>

                    <span>75%</span>

                    <span>100%</span>

                  </div>

                </div>

              </div>

            </div>

          </div>



          {/* Sección Opciones */}

          <div className="mb-5">

            <p className="text-xs font-medium text-brand-600 uppercase tracking-wide mb-3">🌍 Opciones</p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">



              {/* Vía de Compra Global */}

              <div className="bg-white/60 rounded-lg p-3 border border-brand-100">

                <label className="block text-xs font-medium text-slate-700 mb-2">🌍 Vía de Compra</label>

                <div className="flex gap-1.5">

                  <button

                    type="button"

                    onClick={() => setViaCompraGlobal(viaCompraGlobal === 'nacional' ? '' : 'nacional')}

                    className={`flex-1 py-2 px-2 rounded-md border-2 text-xs font-medium transition ${

                      viaCompraGlobal === 'nacional'

                        ? 'bg-green-100 border-green-500 text-green-700'

                        : 'border-slate-300 hover:bg-slate-50 bg-white'

                    }`}

                  >

                    🏠 Nacional

                  </button>

                  <button

                    type="button"

                    onClick={() => setViaCompraGlobal(viaCompraGlobal === 'internacional' ? '' : 'internacional')}

                    className={`flex-1 py-2 px-2 rounded-md border-2 text-xs font-medium transition ${

                      viaCompraGlobal === 'internacional'

                        ? 'bg-brand-100 border-brand-500 text-brand-700'

                        : 'border-slate-300 hover:bg-slate-50 bg-white'

                    }`}

                  >

                    🌍 Internacional

                  </button>

                  <button

                    type="button"

                    onClick={() => setViaCompraGlobal('')}

                    className={`py-2 px-2 rounded-md border-2 text-xs font-medium transition ${

                      viaCompraGlobal === ''

                        ? 'bg-slate-100 border-slate-500 text-slate-700'

                        : 'border-slate-300 hover:bg-slate-50 bg-white'

                    }`}

                  >

                    Mantener

                  </button>

                </div>

              </div>



              {/* Stock Global */}

              <div className="bg-white/60 rounded-lg p-3 border border-brand-100">

                <label className="block text-xs font-medium text-slate-700 mb-2">📦 ¿Tiene Stock?</label>

                <div className="flex gap-1.5">

                  <button

                    type="button"

                    onClick={() => setStockGlobal(stockGlobal === true ? null : true)}

                    className={`flex-1 py-2 px-2 rounded-md border-2 text-xs font-medium transition ${

                      stockGlobal === true

                        ? 'bg-green-100 border-green-500 text-green-700'

                        : 'border-slate-300 hover:bg-slate-50 bg-white'

                    }`}

                  >

                    ✓ Disponible

                  </button>

                  <button

                    type="button"

                    onClick={() => setStockGlobal(stockGlobal === false ? null : false)}

                    className={`flex-1 py-2 px-2 rounded-md border-2 text-xs font-medium transition ${

                      stockGlobal === false

                        ? 'bg-red-100 border-red-500 text-red-700'

                        : 'border-slate-300 hover:bg-slate-50 bg-white'

                    }`}

                  >

                    ✗ Sin stock

                  </button>

                  <button

                    type="button"

                    onClick={() => setStockGlobal(null)}

                    className={`py-2 px-2 rounded-md border-2 text-xs font-medium transition ${

                      stockGlobal === null

                        ? 'bg-slate-100 border-slate-500 text-slate-700'

                        : 'border-slate-300 hover:bg-slate-50 bg-white'

                    }`}

                  >

                    Mantener

                  </button>

                </div>

              </div>

            </div>

          </div>



          {/* Cálculo en tiempo real para edición masiva */}

          {porcentajeUtilidadGlobal !== '' && (() => {
            const costo = Number(precioCostoGlobal || 0);
            const envio = Number(costoEnvioGlobal || 0);
            const util  = Number(porcentajeUtilidadGlobal || 0);
            const precioVenta = costo * (1 + util / 100);
            const margen = precioVenta - costo;
            const iva = precioVenta * 0.16;
            const totalConIva = precioVenta + iva;
            return (
            <div className="mb-5">
              <p className="text-xs font-medium text-brand-600 uppercase tracking-wide mb-3">📊 Vista Previa del Cálculo (por artículo)</p>
              <div className="bg-gradient-to-r from-brand-50 to-brand-50 rounded-lg p-3 border border-brand-200 space-y-2">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm">
                  <div>
                    <p className="text-xs text-slate-500">Costo Base</p>
                    <p className="font-semibold">${costo.toFixed(2)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-brand-600">+ Util. ({util}%)</p>
                    <p className="font-semibold text-brand-600">+${margen.toFixed(2)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-brand-600 font-medium">= P. Venta</p>
                    <p className="font-bold text-brand-700">${precioVenta.toFixed(2)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-orange-500">+ Envío (separado)</p>
                    <p className="font-semibold text-orange-500">+${envio.toFixed(2)}</p>
                  </div>
                </div>
                {/* Estimado con tasa general -- el IVA real lo congela el backend al confirmar */}
                <div className="border-t border-purple-200 pt-2 grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <p className="text-xs text-gray-500">IVA (estimado)</p>
                    <p className="font-semibold text-gray-600">+${iva.toFixed(2)}</p>
                  </div>
                  <div className="bg-white rounded-lg px-2 py-1 border border-blue-200">
                    <p className="text-xs text-green-600 font-medium">Total al Cliente (estimado, c/IVA)</p>
                    <p className="font-bold text-green-700 text-base">${totalConIva.toFixed(2)}</p>
                  </div>
                </div>
              </div>
            </div>
            );
          })()}



          {/* Resumen de Cambios */}

          <div className="mb-4">

            <p className="text-xs font-medium text-brand-600 uppercase tracking-wide mb-2">📋 Resumen</p>

            <div className="bg-white rounded-lg p-3 border border-brand-200 shadow-sm">

              <div className="flex flex-wrap gap-2 text-xs">

                {precioCostoGlobal !== '' && (

                  <div className="bg-green-50 rounded-md px-2 py-1.5 border border-green-200">

                    <span className="text-green-600 font-medium">Costo: ${precioCostoGlobal}</span>

                  </div>

                )}

                {costoEnvioGlobal !== '' && (

                  <div className="bg-brand-50 rounded-md px-2 py-1.5 border border-brand-200">

                    <span className="text-brand-600 font-medium">Envío: ${costoEnvioGlobal}</span>

                  </div>

                )}

                {porcentajeUtilidadGlobal !== '' && (

                  <div className="bg-brand-50 rounded-md px-2 py-1.5 border border-brand-200">

                    <span className="text-brand-600 font-medium">Utilidad: {porcentajeUtilidadGlobal}%</span>

                  </div>

                )}

                {viaCompraGlobal !== '' && (

                  <div className={`rounded-md px-2 py-1.5 border ${viaCompraGlobal === 'nacional' ? 'bg-green-50 border-green-200 text-green-700' : 'bg-brand-50 border-brand-200 text-brand-700'}`}>

                    <span className="font-medium">{viaCompraGlobal === 'nacional' ? '🏠 Nacional' : '🌍 Internacional'}</span>

                  </div>

                )}

                {stockGlobal !== null && (

                  <div className={`rounded-md px-2 py-1.5 border ${stockGlobal ? 'bg-green-50 border-green-200 text-green-700' : 'bg-red-50 border-red-200 text-red-700'}`}>

                    <span className="font-medium">{stockGlobal ? '✓ Stock' : '✗ Sin stock'}</span>

                  </div>

                )}

                {precioCostoGlobal === '' && costoEnvioGlobal === '' && porcentajeUtilidadGlobal === '' && viaCompraGlobal === '' && stockGlobal === null && (

                  <span className="text-slate-400 italic py-1">Sin cambios definidos</span>

                )}

              </div>

            </div>

          </div>



          {/* Botones de acción masiva */}

          <div className="flex gap-3 pt-4 border-t border-brand-200">

            <button

              onClick={() => {

                setModoEdicionMasiva(false);

                setPrecioCostoGlobal('');

                setCostoEnvioGlobal('');

                setViaCompraGlobal('');

                setPorcentajeUtilidadGlobal('');

                setStockGlobal(null);

              }}

              className="px-4 py-2.5 border-2 border-slate-300 rounded-lg hover:bg-slate-50 font-medium text-sm transition"

              disabled={guardandoMasivo}

            >

              Cancelar

            </button>

            <button

              onClick={handleGuardarMasivo}

              disabled={guardandoMasivo || saving}

              className="flex-1 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium text-sm flex items-center justify-center gap-2 disabled:bg-slate-400 transition shadow-md"

            >

              {guardandoMasivo ? (

                <><Loader2 size={16} className="animate-spin" /> Guardando...</>

              ) : (

                <><Save size={16} /> Guardar {articulosSeleccionados.length} artículos</>

              )}

            </button>

          </div>

        </div>

      )}



      {/* Barra de acciones masivas */}
{!readOnly && articulos.length > 0 && !modoEdicionMasiva && (
  <div className="flex items-center justify-between bg-slate-50/80 rounded-xl p-3.5 border border-slate-200/80 shadow-sm">
    <div className="flex items-center gap-3">
      <label className="flex items-center gap-2 cursor-pointer">
        <input
          type="checkbox"
          checked={articulosSeleccionados.length === articulos.length && articulos.length > 0}
          onChange={toggleSeleccionarTodo}
          className="w-4 h-4 text-brand-600 rounded border-slate-300 focus:ring-brand-500/20 cursor-pointer"
        />
        <span className="text-sm font-semibold text-slate-700">
          Seleccionar todos ({articulosSeleccionados.length}/{articulos.length})
        </span>
      </label>
    </div>

    {articulosSeleccionados.length >= 2 && (
      <button
        onClick={() => {
          const primerArticulo = articulos.find(a => a.id === articulosSeleccionados[0]);
          if (primerArticulo) {
            setPrecioCostoGlobal(primerArticulo.precio_unitario?.toString() || '0');
            setCostoEnvioGlobal(primerArticulo.costo_envio?.toString() || '0');
            setPorcentajeUtilidadGlobal((primerArticulo.porcentaje_utilidad ?? 60).toString());
            setViaCompraGlobal(primerArticulo.via_compra || '');
            setStockGlobal(primerArticulo.tiene_stock);
          }
          setModoEdicionMasiva(true);
        }}
        className="px-4 py-2.5 bg-brand-600 text-white rounded-xl hover:bg-brand-700 font-semibold text-sm flex items-center gap-2 transition-all duration-200 shadow-sm active:scale-95"
      >
        <Edit size={16} />
        Editar {articulosSeleccionados.length} seleccionados
      </button>
    )}
  </div>
)}

{/* Tabla de artículos */}
<div className="border border-slate-200/80 rounded-2xl overflow-hidden bg-white shadow-sm">
  {/* Header de la tabla */}
  <div className="grid grid-cols-12 gap-2 p-3.5 bg-slate-50 border-b border-slate-200 text-xs font-bold text-slate-600 uppercase tracking-wider">
    <div className="col-span-1 text-center">✓</div>
    <div className="col-span-3">Artículo</div>
    <div className="col-span-1 text-center">Costo Unit.</div>
    <div className="col-span-1 text-center">Envío</div>
    <div className="col-span-1 text-center">Util.</div>
    <div className="col-span-2 text-center">P. Venta</div>
    <div className="col-span-1 text-center">Stock</div>
    <div className="col-span-1 text-center">Vía</div>
    <div className="col-span-1 text-right">Acción</div>
  </div>

  {/* Filas de artículos */}
  <div className="divide-y divide-slate-100">
    {articulos.map((articulo) => (
      <div key={articulo.id} className="relative">
        {articuloEditandoId === articulo.id ? (
          <ArticuloEditRow
            articulo={articulo}
            onCancel={() => setArticuloEditandoId(null)}
            onSave={onUpdate}
          />
        ) : (
          <ArticuloViewRow
            articulo={articulo}
            onEdit={() => {
              setArticulosSeleccionados([articulo.id]);
              setArticuloEditandoId(articulo.id);
            }}
            onVerArticulo={() => handleVerArticulo(articulo.articulo_id)}
            isSelected={articulosSeleccionados.includes(articulo.id)}
            onToggleSelect={toggleSeleccionarArticulo}
            disableIndividualEdit={articulosSeleccionados.length >= 2 || modoEdicionMasiva}
          />
        )}
      </div>
    ))}
  </div>
</div>

</div>
</>
  );
};





// ==================== MODAL DE COTIZACIÓN ====================

const CotizacionModal = ({ cotizacion: cotizacionInicial, onClose, onRefresh, onPrint, onEnviarEmail, onEnviarFactura, onPasarAFactura }) => {

  // Formatear artículos al inicializar

  const [cotizacion, setCotizacion] = useState(() => {

    const formateada = {

      ...cotizacionInicial,

      articulos: formatearArticulos(cotizacionInicial.articulos)

    };

    return formateada;

  });

  const [loading, setLoading] = useState(false);

  const [actionLoading, setActionLoading] = useState(false);

  const [condicionesPago, setCondicionesPago] = useState(cotizacion.condiciones_pago || '');
  const [guardandoCondiciones, setGuardandoCondiciones] = useState(false);
  const [showFacturaModal, setShowFacturaModal] = useState(false);
  const [pasandoAFactura, setPasandoAFactura] = useState(false);

  const { notify: notifyModal, confirm: confirmModal, prompt: promptModal, ModalRenderer: CotizacionModalRenderer } = useModal();

  const handleGuardarCondiciones = async () => {
    setGuardandoCondiciones(true);
    try {
      await cotizacionesService.update(cotizacion.id, { condiciones_pago: condicionesPago });
      setCotizacion(prev => ({ ...prev, condiciones_pago: condicionesPago }));
    } catch (error) {
      console.error('Error al guardar condiciones de pago:', error);
      await notifyModal('error', 'No se pudieron guardar las condiciones', error.message || 'Intenta de nuevo en unos segundos.');
    } finally {
      setGuardandoCondiciones(false);
    }
  };

  // Hook de validación de stock (nuevo flujo)
  const { 
    procesarConStock, 
    validando: validandoStock,
    mostrarNotificacionResultado 
  } = useStockValidation();



  // Una vez confirmada (o mas adelante en el flujo: en espera de pago,
  // pagada, o ya pasada a factura) ya no tiene sentido poder editar
  // articulos/condiciones ni volver a pulsar Confirmar/Rechazar.
  const esReadOnly = ['confirmada', 'rechazada', 'en_espera_pago', 'pagada'].includes(cotizacion.estado)
    || cotizacion.pasada_a_factura === true;



  // Calcular completitud considerando todos los campos necesarios

  const calcularCompletitud = () => {

    const articulos = cotizacion.articulos || [];

    if (articulos.length === 0) return 0;



    const articulosCompletos = articulos.filter(a => {

      const tienePrecio = (a.precio_unitario || 0) > 0;

      // El stock se consulta contra el inventario real (ArticuloEditRow),
      // no lo completa el usuario -- no cuenta para la completitud. Con
      // precio, envío y vía de compra ya se puede cotizar, haya o no stock.

      const tieneCostoEnvio = (a.costo_envio || 0) >= 0;

      const tieneViaCompra = !!a.via_compra;



      return tienePrecio && tieneCostoEnvio && tieneViaCompra;

    }).length;



    return Math.round((articulosCompletos / articulos.length) * 100);

  };



  const completitud = calcularCompletitud();

  const puedeConfirmar = completitud === 100;



  // Recargar cotización después de actualizar

  const recargarCotizacion = async () => {

    try {

      const result = await cotizacionesService.getById(cotizacion.id);

      // Manejar diferentes estructuras de respuesta

      let data = result;

      if (result.success !== undefined && result.data) {

        data = result.data;

      } else if (result.data && !result.articulos) {

        data = result.data.data || result.data;

      }

      if (data) {

        // Formatear artículos con valores por defecto

        const cotizacionFormateada = {

          ...data,

          articulos: formatearArticulos(data.articulos || [])

        };

        setCotizacion(cotizacionFormateada);

      }

    } catch (error) {

      console.error('Error al recargar cotización:', error);

    }

  };



  const handleUpdateArticulo = async (cotizacionId, articuloId, datos, options = {}) => {

    const { skipRecarga } = options;

    setLoading(true);

    try {

      // Si no hay articuloId, solo recargar (caso de edición masiva completada)

      if (!articuloId) {

        if (!skipRecarga) {

          await recargarCotizacion();

        }

        if (onRefresh) onRefresh();

        return;

      }

      

      const result = await cotizacionesService.updateArticulo(cotizacionId, articuloId, datos);

      const data = result.success !== undefined ? result.data : result;

      if (data && !skipRecarga) {

        await recargarCotizacion();

        if (onRefresh) onRefresh();

      }

    } catch (error) {

      throw error;

    } finally {

      setLoading(false);

    }

  };



  const handleConfirmar = async () => {

    if (!puedeConfirmar) {
      await notifyModal('warning', 'Cotización incompleta', 'La cotización debe estar al 100% completa para poder confirmarla. Completa todos los artículos primero.');
      return;
    }

    const ok = await confirmModal(
      `¿Confirmar cotización ${cotizacion.numero}?`,
      'Esta acción marcará la cotización como confirmada. Se validará el stock de todos los artículos.'
    );
    if (!ok) return;

    setActionLoading(true);

    try {

      // ========== NUEVO: Validar stock antes de confirmar ==========
      const articulosParaValidar = cotizacion.articulos.map(art => ({
        articulo_id: art.articulo_id,
        cantidad: art.cantidad,
        nombre: art.nombre,
        via_compra: art.via_compra,
        link_proveedor: art.link_proveedor
      }));

      const resultadoStock = await procesarConStock({
        cotizacionId: cotizacion.id,
        cotizacionNumero: cotizacion.numero,
        clienteNombre: cotizacion.cliente_nombre,
        empresaNombre: cotizacion.empresa_nombre,
        articulos: articulosParaValidar,
        accion: 'aceptar'
      });

      if (!resultadoStock.exito) {
        await notifyModal('error', 'Error en validación de stock', resultadoStock.error || 'Error en validación de stock');
        setActionLoading(false);
        return;
      }

      // Mostrar notificación del resultado de stock
      mostrarNotificacionResultado(resultadoStock);
      // ========== FIN NUEVO ==========

      const response = await cotizacionesService.confirmar(cotizacion.id);

      await notifyModal('success', '¡Cotización confirmada!', response.message || 'La cotización fue confirmada exitosamente.');

      if (onRefresh) onRefresh();

      onClose();

    } catch (error) {

      await notifyModal('error', 'Error al confirmar', error.message || 'No se pudo confirmar la cotización.');

    } finally {

      setActionLoading(false);

    }

  };



  const handleRechazar = async () => {

    const motivo = await promptModal(
      'Rechazar cotización',
      `¿Estás seguro que deseas rechazar la cotización ${cotizacion.numero}?`,
      'Motivo del rechazo (obligatorio)',
      'Ej: Precio fuera de rango, cliente canceló...'
    );

    if (!motivo || motivo.trim() === '') return;

    setActionLoading(true);

    try {

      await cotizacionesService.rechazar(cotizacion.id, motivo);

      await notifyModal('info', 'Cotización rechazada', 'La cotización fue marcada como rechazada correctamente.');

      if (onRefresh) onRefresh();

      onClose();

    } catch (error) {

      await notifyModal('error', 'Error al rechazar', error.message || 'No se pudo rechazar la cotización.');

    } finally {

      setActionLoading(false);

    }

  };



  return (
    <>
    {CotizacionModalRenderer}
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">

      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[95vh] flex flex-col overflow-hidden">

        {/* Header */}

        <div className="bg-gradient-to-r from-brand-600 to-brand-700 text-white p-5">

          <div className="flex justify-between items-start">

            <div>

              <div className="flex items-center gap-3 mb-1">

                <FileText size={24} />

                <h2 className="text-xl font-bold">{cotizacion.numero}</h2>

                <EstadoChip estado={cotizacion.estado} />

              </div>

              <p className="text-brand-100 flex items-center gap-2">

                <Building2 size={16} /> {cotizacion.empresa_nombre}

              </p>

            </div>

            <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-lg"><X size={24} /></button>

          </div>

        </div>



        {/* Contenido */}

        <div className="flex-1 overflow-y-auto p-5">

          {/* Por qué está bloqueada la edición */}
          {esReadOnly && (
            <div className="mb-5 p-4 bg-slate-50 border border-slate-200 rounded-xl flex items-start gap-3">
              <Info size={18} className="text-slate-400 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-slate-600">
                {cotizacion.pasada_a_factura
                  ? 'Esta cotización ya generó una factura y no se puede modificar. Cualquier ajuste va en una nota de crédito sobre esa factura.'
                  : `Esta cotización está "${ESTADOS_CONFIG[cotizacion.estado]?.label || cotizacion.estado}" y ya no se puede editar ni volver a confirmar o rechazar.`}
              </p>
            </div>
          )}

          {/* Info Solicitud */}

          <div className="bg-brand-50 border border-brand-200 rounded-xl p-4 mb-5">

            <h3 className="font-semibold text-brand-800 mb-2 flex items-center gap-2">

              <ShoppingCart size={18} /> Solicitud de Origen

            </h3>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">

              <div><span className="text-brand-600">ID Solicitud:</span> <strong>#{cotizacion.solicitud_id}</strong></div>

              <div><span className="text-brand-600">Nombre:</span> <strong>{cotizacion.solicitud_nombre || '-'}</strong></div>

              <div><span className="text-brand-600">Creado:</span> <strong>{formatearFecha(cotizacion.fecha_creacion)}</strong></div>

              <div><span className="text-brand-600">Por:</span> <strong>{cotizacion.usuario_creador_nombre || '-'}</strong></div>

            </div>

            {cotizacion.cliente_nombre && (

              <div className="mt-2 pt-2 border-t border-brand-200">

                <span className="text-brand-600 text-sm">Cliente:</span> <strong className="text-sm">{cotizacion.cliente_nombre}</strong>

                {cotizacion.cliente_rif && <span className="text-sm text-slate-500 ml-2">({cotizacion.cliente_rif})</span>}

              </div>

            )}

          </div>



          {/* Barra de progreso */}

          <div className="mb-5">

            <div className="flex justify-between text-sm mb-1">

              <span className="text-slate-600">Completitud de cotización</span>

              <span className={`font-bold ${completitud === 100 ? 'text-green-600' : 'text-orange-600'}`}>{completitud}%</span>

            </div>

            <div className="w-full bg-slate-200 rounded-full h-3">

              <div className={`h-3 rounded-full transition-all ${completitud === 100 ? 'bg-green-500' : completitud >= 50 ? 'bg-yellow-500' : 'bg-orange-500'}`}

                style={{ width: `${completitud}%` }} />

            </div>

            {completitud < 100 && (

              <div className="mt-2 p-3 bg-orange-50 border border-orange-200 rounded-lg">

                <p className="text-sm text-orange-700 flex items-center gap-1 font-medium mb-1">

                  <AlertTriangle size={14} />

                  Para completar la cotización, cada artículo necesita:

                </p>

                <ul className="text-xs text-orange-600 ml-5 list-disc space-y-0.5">

                  <li>Costo unitario (precio del proveedor)</li>

                  <li>Costo de envío</li>

                  <li>Vía de compra (Nacional o Internacional)</li>

                </ul>

              </div>

            )}

          </div>



          {/* Artículos */}

          <div className="mb-5">

            <h3 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">

              <Package size={20} className="text-brand-600" />

              Artículos ({cotizacion.articulos?.length || 0})

            </h3>

            <ArticulosListManager

              articulos={cotizacion.articulos}

              cotizacionId={cotizacion.id}

              onUpdate={handleUpdateArticulo}

              readOnly={esReadOnly}

              saving={loading}

              cotizacionInfo={{
                numero_cotizacion: cotizacion.numero,
                cliente_nombre: cotizacion.cliente_nombre,
                empresa_nombre: cotizacion.empresa_nombre
              }}

            />

          </div>



          {/* Totales */}

          <div className="bg-slate-50 rounded-xl p-4 border">

            <h3 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">

              <DollarSign size={20} className="text-green-600" /> Resumen Financiero

            </h3>



            {/* Calcular totales */}

            {(() => {

              const articulos = cotizacion.articulos || [];

              // La API devuelve los numeric de Postgres como texto (ej. "32.0000")
              // para no perder precision. Sumarlos con + sin parseFloat concatena
              // texto en vez de sumar y termina en NaN.
              const totalCostos = articulos.reduce((sum, a) => sum + ((parseFloat(a.precio_unitario) || 0) * (parseFloat(a.cantidad) || 1)), 0);

              // El envio es un costo por articulo (linea), no por unidad/cantidad.
              const totalEnvios = articulos.reduce((sum, a) => sum + (parseFloat(a.costo_envio) || 0), 0);

              // margen_ganancia ya es el total del renglon (columna generada: subtotal - costo_total),
              // no un valor por unidad -- no se multiplica por cantidad.
              const totalMargen = articulos.reduce((sum, a) => sum + (parseFloat(a.margen_ganancia) || 0), 0);

              const subtotalVenta = articulos.reduce((sum, a) => sum + (parseFloat(a.subtotal) || 0), 0);

              // El backend calcula y congela el IVA con la alicuota vigente real
              // (ver CLAUDE.md: "ningun porcentaje fiscal en el codigo"). Mientras
              // la cotizacion sigue en borrador y esos campos aun no existen,
              // mostramos un estimado local con la tasa general y lo marcamos
              // como tal -- nunca reemplazamos el valor ya congelado.
              const ivaCongelado = parseFloat(cotizacion.impuestos);
              const totalCongelado = parseFloat(cotizacion.total);
              const tieneValoresCongelados = Number.isFinite(ivaCongelado) && Number.isFinite(totalCongelado);

              const ivaEstimado = subtotalVenta * 0.16;
              const iva = tieneValoresCongelados ? ivaCongelado : ivaEstimado;
              const total = tieneValoresCongelados ? totalCongelado : (subtotalVenta + ivaEstimado);

              // subtotal/envio: se prefiere lo que ya persistio el backend
              // (fn_recalcular_cotizacion) en vez de recalcularlo aqui; el
              // fallback local solo cubre antes de que existan esos campos.
              const subtotalMostrado = parseFloat(cotizacion.subtotal ?? subtotalVenta) || 0;
              const envioMostrado = parseFloat(cotizacion.envio ?? totalEnvios) || 0;

              // Margen bruto % = ganancia / (venta + envio cobrado), sin IVA
              // (misma formula que cotizacionCalculos.js en el backend).
              const baseMargen = subtotalVenta + totalEnvios;
              const margenBrutoPct = baseMargen > 0 ? ((totalMargen / baseMargen) * 100).toFixed(2) : '0.00';

              const porcentajeMargenPromedio = totalCostos > 0 ? ((totalMargen / (totalCostos + totalEnvios)) * 100).toFixed(1) : 0;



              return (

                <div className="space-y-3">

                  {/* Desglose de costos */}

                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 p-3 bg-white rounded-lg border">

                    <div>

                      <p className="text-xs text-slate-500">Total Costos</p>

                      <p className="font-semibold text-slate-700">{formatearMoneda(totalCostos)}</p>

                    </div>

                    <div>

                      <p className="text-xs text-slate-500">Total Envíos</p>

                      <p className="font-semibold text-orange-600">{formatearMoneda(totalEnvios)}</p>

                    </div>

                    <div>

                      <p className="text-xs text-slate-500">Costo Total</p>

                      <p className="font-semibold text-slate-800">{formatearMoneda(totalCostos + totalEnvios)}</p>

                    </div>

                    <div className="bg-green-50 rounded-lg p-2 -m-1">

                      <p className="text-xs text-green-600">Ganancia</p>

                      <p className="font-bold text-green-700">{formatearMoneda(totalMargen)}</p>

                      <p className="text-xs text-green-600">Margen bruto: {margenBrutoPct}%</p>

                    </div>

                  </div>



                  {/* Totales de venta */}

                  <div className="space-y-2 pt-2 border-t">

                    <div className="flex justify-between">

                      <span className="text-slate-600">Subtotal (Precio Venta):</span>

                      <span className="font-semibold">{formatearMoneda(subtotalMostrado)}</span>

                    </div>

                    <div className="flex justify-between">

                      <span className="text-slate-600">Envío:</span>

                      <span className="font-semibold text-orange-600">{formatearMoneda(envioMostrado)}</span>

                    </div>

                    <div className="flex justify-between">

                      <span className="text-gray-600">IVA{tieneValoresCongelados ? '' : ' (estimado)'}:</span>

                      <span className="font-semibold">{formatearMoneda(iva)}</span>

                    </div>

                    <div className="flex justify-between border-t-2 border-slate-300 pt-2 mt-2">

                      <span className="text-lg font-bold">TOTAL A COBRAR{tieneValoresCongelados ? '' : ' (estimado)'}:</span>

                      <span className="text-2xl font-bold text-green-600">{formatearMoneda(total + totalEnvios)}</span>

                    </div>

                    {!tieneValoresCongelados && (
                      <p className="text-xs text-gray-400 italic -mt-1">
                        Estimado con la tasa general de IVA. El backend congela el valor real al confirmar.
                      </p>
                    )}

                  </div>

                </div>

              );

            })()}

          </div>



          {!esReadOnly && (
            <div className="mt-4 p-4 bg-white rounded-xl border">
              <label className="block text-sm font-semibold text-slate-800 mb-2">
                Condiciones de pago (aparece en el PDF, editable antes de enviar)
              </label>
              <textarea
                value={condicionesPago}
                onChange={(e) => setCondicionesPago(e.target.value)}
                placeholder="Ej: 50% de anticipo, 50% contra entrega. Válido por 15 días."
                rows={2}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-brand-500 focus:outline-none"
              />
              <div className="flex justify-end mt-2">
                <button
                  onClick={handleGuardarCondiciones}
                  disabled={guardandoCondiciones || condicionesPago === (cotizacion.condiciones_pago || '')}
                  className="px-4 py-1.5 bg-brand-600 text-white rounded-lg hover:bg-brand-700 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {guardandoCondiciones ? 'Guardando…' : 'Guardar condiciones'}
                </button>
              </div>
            </div>
          )}

          {cotizacion.motivo_rechazo && (

            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">

              <p className="text-sm text-red-700"><strong>Motivo de rechazo:</strong> {cotizacion.motivo_rechazo}</p>

            </div>

          )}

        </div>



        {/* Footer */}

        <div className="p-5 border-t bg-slate-50">

          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">

            {completitud === 100 && (

              <div className="flex flex-wrap items-center gap-3">

                <button

                  onClick={() => onPrint(cotizacion)}

                  className="px-4 py-2.5 bg-brand-600 text-white rounded-lg hover:bg-brand-700 font-medium flex items-center gap-2 shadow-md hover:shadow-lg transition-all"

                >

                  <FileText size={18} /> Imprimir / PDF

                </button>

                <button

                  onClick={() => onEnviarEmail(cotizacion)}

                  className="px-4 py-2.5 bg-gradient-to-r from-brand-600 to-brand-700 text-white rounded-lg hover:from-brand-700 hover:to-brand-800 font-medium flex items-center gap-2 shadow-md hover:shadow-lg transition-all"

                >

                  <Send size={18} /> Enviar por Email

                </button>

                {puedeConfirmar && onPasarAFactura && !(cotizacion.pasada_a_factura === true || cotizacion.pasada_a_factura === 1) && (

                  <button

                    onClick={async () => {
                      // El backend ya queda marcado pasada_a_factura=true,
                      // pero este modal tiene su propia copia local de la
                      // cotizacion (no se entera sola) -- hay que recargarla
                      // para que el boton pase a "Generar Factura" y, de
                      // una vez, abrir el modal de facturacion.
                      setPasandoAFactura(true);
                      try {
                        const ok = await onPasarAFactura(cotizacion);
                        if (ok) {
                          await recargarCotizacion();
                          setShowFacturaModal(true);
                        }
                      } finally {
                        setPasandoAFactura(false);
                      }
                    }}

                    disabled={pasandoAFactura}

                    className="px-4 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium flex items-center gap-2 shadow-md hover:shadow-lg transition-all disabled:opacity-50"

                  >

                    {pasandoAFactura ? <Loader2 size={18} className="animate-spin" /> : <FileCheck size={18} />} Pasar a Factura

                  </button>

                )}

                {(cotizacion.pasada_a_factura === true || cotizacion.pasada_a_factura === 1) && (

                  <button

                    onClick={() => setShowFacturaModal(true)}

                    className="px-4 py-2.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 font-medium flex items-center gap-2 shadow-md hover:shadow-lg transition-all"

                  >

                    <FileCheck size={18} /> Generar Factura

                  </button>

                )}

              </div>

            )}

            {showFacturaModal && (
              <FacturaEmisionModal
                cotizacion={cotizacion}
                onClose={() => setShowFacturaModal(false)}
                onEmitida={() => { if (onRefresh) onRefresh(); }}
              />
            )}

            <div className="flex items-center gap-3 ml-auto">

              <button onClick={onClose} className="px-5 py-2.5 border-2 border-slate-300 rounded-lg hover:bg-slate-100 font-medium transition-colors">Cerrar</button>

              {!esReadOnly && (

                <>

                  <button onClick={handleRechazar} disabled={actionLoading}

                    className="px-5 py-2 border-2 border-red-500 text-red-600 rounded-lg hover:bg-red-50 font-medium flex items-center gap-2 disabled:opacity-50"

                  >

                    <XCircle size={18} /> Rechazar

                  </button>

                  <button onClick={handleConfirmar} disabled={actionLoading || validandoStock || !puedeConfirmar}

                    className={`px-5 py-2 rounded-lg font-medium flex items-center gap-2 ${puedeConfirmar ? 'bg-green-600 text-white hover:bg-green-700' : 'bg-slate-300 text-slate-500 cursor-not-allowed'}`}>

                {(actionLoading || validandoStock) ? <Loader2 size={18} className="animate-spin" /> : <Check size={18} />}

                {validandoStock ? 'Verificando Stock...' : 'Confirmar Cotización'}

              </button>

            </>

                 )}

        </div>

          </div>

        </div>

      </div>

    </div>
    </>
  );

};



// ==================== MODAL DE ENVÍO DE COTIZACIÓN POR EMAIL ====================

const EmailSendModal = ({ cotizacion, onClose, onEnviar }) => {

  const { notify: notifyEmail, ModalRenderer: EmailModalRenderer } = useModal();

  const [emailDestino, setEmailDestino] = useState('');

  const [asunto, setAsunto] = useState(`Cotización ${cotizacion.numero || ''}`);

  const [mensaje, setMensaje] = useState(

    `Estimado cliente,\n\nAdjunto encontrará la cotización correspondiente.\n\n` +

    `Número: ${cotizacion.numero || ''}\n` +

    `Empresa: ${cotizacion.empresa_nombre || ''}\n` +

    `Total: ${formatearMoneda(cotizacion.total || 0)}\n\n` +

    `Quedo atento a sus comentarios.\n\nSaludos cordiales.`

  );

  const [enviando, setEnviando] = useState(false);



  const handleEnviar = async () => {

    if (!emailDestino.trim()) {
      await notifyEmail('warning', 'Campo requerido', 'Debe ingresar un email destinatario para continuar.');
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailDestino)) {
      await notifyEmail('warning', 'Email inválido', 'El email ingresado no tiene un formato válido. Verifícalo e intenta de nuevo.');
      return;
    }

    setEnviando(true);

    try {

      await onEnviar({

        cotizacionId: cotizacion.id,

        email_destino: emailDestino.trim(),

        asunto: asunto.trim(),

        mensaje: mensaje.trim()

      });

      onClose();

    } catch (error) {

      // Error ya mostrado en onEnviar

    } finally {

      setEnviando(false);

    }

  };



  return (
    <>
    {EmailModalRenderer}
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[60] p-4">

      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col overflow-hidden">

        {/* Header */}

        <div className="bg-gradient-to-r from-brand-400 to-brand-900 text-white p-5">

          <div className="flex justify-between items-start">

            <div className="flex items-center gap-3">

              <Mail size={24} />

              <h2 className="text-xl font-bold">Enviar Cotización por Email</h2>

            </div>

            <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-lg">

              <X size={24} />

            </button>

          </div>

          <p className="text-brand-100 text-sm mt-1">

            {cotizacion.numero} • {cotizacion.empresa_nombre}

          </p>

        </div>



        {/* Contenido */}

        <div className="flex-1 overflow-y-auto p-5 space-y-4">

        {/* Email Destino */}

          <div>

            <label className="block text-sm font-medium text-slate-700 mb-1">

              📧 Email Destinatario <span className="text-red-900">*</span>

            </label>

            <div className="relative">

              <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />

              <input

                type="email"

                value={emailDestino}

                onChange={(e) => setEmailDestino(e.target.value)}

                placeholder="cliente@ejemplo.com"

                className="w-full pl-10 pr-4 py-2.5 border-2 border-slate-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"

              />

            </div>

          </div>



          {/* Asunto */}

          <div>

            <label className="block text-sm font-medium text-slate-700 mb-1">

              📝 Asunto

            </label>

            <input

              type="text"

              value={asunto}

              onChange={(e) => setAsunto(e.target.value)}

              className="w-full px-4 py-2.5 border-2 border-slate-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"

            />

          </div>



          {/* Mensaje */}

          <div>

            <label className="block text-sm font-medium text-slate-700 mb-1">

              💬 Mensaje

            </label>

            <textarea

              value={mensaje}

              onChange={(e) => setMensaje(e.target.value)}

              rows={6}

              className="w-full px-4 py-2.5 border-2 border-slate-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 resize-none"

            />

          </div>



          {/* Info adicional */}

          <div className="bg-brand-50 rounded-lg p-3 border border-brand-200">

            <p className="text-sm text-brand-700 flex items-start gap-2">

              <AlertCircle size={16} className="flex-shrink-0 mt-0.5" />

              <span>

                El PDF de la cotización se generará automáticamente y se adjuntará al correo.

              </span>

            </p>

          </div>

        </div>



        {/* Footer */}

        <div className="p-5 border-t bg-slate-50 flex justify-end gap-3">

          <button

            onClick={onClose}

            className="px-5 py-2 border-2 rounded-lg hover:bg-slate-100 font-medium"

            disabled={enviando}

          >

            Cancelar

          </button>

          <button

            onClick={handleEnviar}

            disabled={enviando}

            className="px-5 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 font-medium flex items-center gap-2 disabled:bg-slate-400"

          >

            {enviando ? (

              <><Loader2 size={18} className="animate-spin" /> Enviando...</>

            ) : (

              <><Send size={18} /> Enviar Email</>

            )}

          </button>

        </div>

      </div>

    </div>

    </>

  );

};



// ==================== MODAL DE ENVÍO DE FACTURA POR EMAIL ====================

const FacturaEmailModal = ({ cotizacion, onClose, onEnviar }) => {

  const { notify: notifyFactura, ModalRenderer: FacturaEmailModalRenderer } = useModal();

  const [emailDestino, setEmailDestino] = useState(cotizacion.cliente_email || '');

  const [asunto, setAsunto] = useState(`Factura - Cotización ${cotizacion.numero || ''}`);

  const [mensaje, setMensaje] = useState(

    `Estimado cliente,\n\nAdjunto encontrará la factura correspondiente a su cotización.\n\n` +

    `Cotización: ${cotizacion.numero || ''}\n` +

    `Empresa: ${cotizacion.empresa_nombre || ''}\n` +

    `Total: ${formatearMoneda(cotizacion.total || 0)}\n\n` +

    `Gracias por su preferencia.\n\nSaludos cordiales.`

  );

  const [enviando, setEnviando] = useState(false);



  const handleEnviar = async () => {

    if (!emailDestino.trim()) {
      await notifyFactura('warning', 'Campo requerido', 'Debe ingresar un email destinatario para continuar.');
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailDestino)) {
      await notifyFactura('warning', 'Email inválido', 'El email ingresado no tiene un formato válido. Verifícalo e intenta de nuevo.');
      return;
    }

    setEnviando(true);

    try {

      await onEnviar({

        cotizacionId: cotizacion.id,

        email_destino: emailDestino.trim(),

        asunto: asunto.trim(),

        mensaje: mensaje.trim()

      });

      onClose();

    } catch (error) {

      await notifyFactura('error', 'Error', 'No se pudo enviar la factura. Intente de nuevo.');

    } finally {

      setEnviando(false);

    }

  };



  return (
    <>
    {FacturaEmailModalRenderer}
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[60] p-4">

      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col overflow-hidden">

        {/* Header */}

        <div className="bg-gradient-to-r from-brand-600 to-brand-900 text-white p-5">

          <div className="flex justify-between items-start">

            <div className="flex items-center gap-3">

              <FileCheck size={24} />

              <h2 className="text-xl font-bold">Enviar Factura por Email</h2>

            </div>

            <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-lg">

              <X size={24} />

            </button>

          </div>

          <p className="text-brand-100 text-sm mt-1">

            {cotizacion.numero} • {cotizacion.empresa_nombre}

          </p>

        </div>



        {/* Contenido */}

        <div className="flex-1 overflow-y-auto p-5 space-y-4">

          {/* Email Destino */}

          <div>

            <label className="block text-sm font-medium text-slate-700 mb-1">

              📧 Email Destinatario <span className="text-red-500">*</span>

            </label>

            <div className="relative">

              <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />

              <input

                type="email"

                value={emailDestino}

                onChange={(e) => setEmailDestino(e.target.value)}

                placeholder="cliente@ejemplo.com"

                className="w-full pl-10 pr-4 py-2.5 border-2 border-slate-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"

              />

            </div>

            {cotizacion.cliente_email && (

              <p className="text-xs text-slate-500 mt-1">Email del cliente pre-cargado</p>

            )}

          </div>



          {/* Asunto */}

          <div>

            <label className="block text-sm font-medium text-slate-700 mb-1">

              📝 Asunto

            </label>

            <input

              type="text"

              value={asunto}

              onChange={(e) => setAsunto(e.target.value)}

              className="w-full px-4 py-2.5 border-2 border-slate-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"

            />

          </div>



          {/* Mensaje */}

          <div>

            <label className="block text-sm font-medium text-slate-700 mb-1">

              💬 Mensaje

            </label>

            <textarea

              value={mensaje}

              onChange={(e) => setMensaje(e.target.value)}

              rows={6}

              className="w-full px-4 py-2.5 border-2 border-slate-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 resize-none"

            />

          </div>



          {/* Info adicional */}

          <div className="bg-brand-50 rounded-lg p-3 border border-brand-200">

            <p className="text-sm text-brand-700 flex items-start gap-2">

              <AlertCircle size={16} className="flex-shrink-0 mt-0.5" />

              <span>

                La factura PDF se generará automáticamente y se adjuntará al correo que usted Ingreso.

              </span>

            </p>

          </div>

        </div>



        {/* Footer */}

        <div className="p-5 border-t bg-slate-50 flex justify-end gap-3">

          <button

            onClick={onClose}

            className="px-5 py-2 border-2 rounded-lg hover:bg-slate-100 font-medium"

            disabled={enviando}

          >

            Cancelar

          </button>

          <button

            onClick={handleEnviar}

            disabled={enviando}

            className="px-5 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 font-medium flex items-center gap-2 disabled:bg-slate-400"

          >

            {enviando ? (

              <><Loader2 size={18} className="animate-spin" /> Enviando...</>

            ) : (

              <><Send size={18} /> Enviar Factura</>

            )}

          </button>

        </div>

      </div>

    </div>

    </>

  );

};



// ==================== ÁRBOL DE EMPRESAS ====================

const EmpresaTreeItem = ({ empresa, cotizaciones, onSelectCotizacion, expanded, onToggle, onPrint, onEnviarEmail, onEnviarFactura, onPasarAFactura, selectedCotizacionIds, onToggleSeleccion, puedeSeleccionarseParaCombinar }) => {

  const totalMonto = cotizaciones.reduce((sum, c) => sum + (parseFloat(c.total) || 0), 0);

  const pendientes = cotizaciones.filter(c => !['confirmada', 'rechazada'].includes(c.estado)).length;



  return (

    <div className="border rounded-xl overflow-hidden bg-white shadow-sm mb-3">

      {/* Header Empresa */}

      <div onClick={onToggle}

        className="flex items-center justify-between p-4 cursor-pointer hover:bg-slate-50 transition">

        <div className="flex items-center gap-3">

          <div className={`p-2 rounded-lg transition ${expanded ? 'bg-brand-100' : 'bg-slate-100'}`}>

            {expanded ? <ChevronDown size={20} className="text-brand-600" /> : <ChevronRightIcon size={20} className="text-slate-500" />}

          </div>

          <Building2 size={24} className="text-brand-600" />

          <div>

            <h3 className="font-bold text-slate-800">{empresa.nombre}</h3>

            <p className="text-sm text-slate-500">{cotizaciones.length} cotización(es)</p>

          </div>

        </div>

        <div className="flex items-center gap-4">

          {pendientes > 0 && (

            <span className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded-full text-xs font-medium">

              {pendientes} pendiente(s)

            </span>

          )}

          <div className="text-right">

            <p className="text-sm text-slate-500">Total</p>

            <p className="font-bold text-green-600">{formatearMoneda(totalMonto)}</p>

          </div>

        </div>

      </div>



      {/* Cotizaciones: tabla compacta de una linea, misma logica que el
          listado de Solicitudes -- el detalle completo sigue disponible
          al hacer clic en la fila (abre el modal via onSelectCotizacion). */}

      {expanded && (

        <div className="border-t bg-gray-50 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-gray-500">
              <tr>
                <th className="px-3 py-2 w-8"></th>
                <th className="px-3 py-2">Número</th>
                <th className="px-3 py-2">Solicitud</th>
                <th className="px-3 py-2">Estado</th>
                <th className="px-3 py-2">Completitud</th>
                <th className="px-3 py-2 text-right">Total</th>
                <th className="px-3 py-2">Fecha</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {cotizaciones.map(cot => {
                const completitud = cot.completitud || 0;
                const totalArticulos = cot.total_articulos || cot.articulos?.length || 0;
                const combinable = puedeSeleccionarseParaCombinar ? puedeSeleccionarseParaCombinar(cot) : false;
                const seleccionada = selectedCotizacionIds?.includes(cot.id);

                return (
                  <tr key={cot.id} onClick={() => onSelectCotizacion(cot)}
                    className={`bg-white cursor-pointer hover:bg-blue-50 transition ${seleccionada ? 'bg-amber-50' : ''}`}>
                    <td className="px-3 py-2">
                      {onToggleSeleccion && (
                        <input
                          type="checkbox"
                          checked={!!seleccionada}
                          disabled={!combinable}
                          onClick={(e) => e.stopPropagation()}
                          onChange={() => onToggleSeleccion(cot)}
                          title={combinable ? 'Seleccionar para combinar con otra cotización' : 'Solo se pueden combinar cotizaciones en borrador o en proceso'}
                          className="w-4 h-4 text-amber-600 border-gray-300 rounded disabled:opacity-30 disabled:cursor-not-allowed"
                        />
                      )}
                    </td>
                    <td className="px-3 py-2 font-bold text-blue-600 whitespace-nowrap">{cot.numero}</td>
                    <td className="px-3 py-2 max-w-xs">
                      <p className="truncate text-gray-700" title={cot.solicitud_nombre || ''}>
                        {cot.solicitud_nombre || <span className="text-gray-400 italic">Sin nombre</span>}
                      </p>
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <EstadoChip estado={cot.estado} size="small" />
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <div className="flex items-center gap-2 min-w-[7rem]">
                        <div className="flex-1 bg-gray-200 rounded-full h-1.5">
                          <div className={`h-1.5 rounded-full ${completitud === 100 ? 'bg-green-500' : 'bg-orange-400'}`}
                            style={{ width: `${completitud}%` }} />
                        </div>
                        <span className="text-xs text-gray-500 whitespace-nowrap">{completitud}% · {totalArticulos}</span>
                      </div>
                    </td>
                    <td className="px-3 py-2 text-right font-bold text-green-700 whitespace-nowrap">{formatearMoneda(cot.total)}</td>
                    <td className="px-3 py-2 text-xs text-gray-500 whitespace-nowrap">{formatearFecha(cot.fecha_creacion)}</td>
                    <td className="px-3 py-2">
                      {completitud === 100 && (
                        <div className="flex items-center gap-1 justify-end">
                          <button
                            onClick={(e) => { e.stopPropagation(); onPrint(cot); }}
                            className="p-1.5 bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition"
                            title="Imprimir / Descargar Cotización"
                          >
                            <FileText size={14} />
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); onEnviarEmail(cot); }}
                            className="p-1.5 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded hover:from-blue-600 hover:to-blue-700 transition shadow-sm"
                            title="Enviar por Email"
                          >
                            <Send size={14} />
                          </button>
                          {cot.estado === 'confirmada' && onPasarAFactura && !(cot.pasada_a_factura === true || cot.pasada_a_factura === 1) && (
                            <button
                              onClick={(e) => { e.stopPropagation(); onPasarAFactura(cot); }}
                              className="p-1.5 bg-green-100 text-green-700 rounded hover:bg-green-200 transition"
                              title="Pasar a Factura"
                            >
                              <FileCheck size={14} />
                            </button>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

      )}

    </div>

  );

};



// ==================== COMPONENTE PRINCIPAL ====================

const CotizacionesView = () => {
  const { notify, confirm: confirmModal, ModalRenderer } = useModal();

  const [cotizacionesPorEmpresa, setCotizacionesPorEmpresa] = useState([]);

  const [stats, setStats] = useState(null);

  const [loading, setLoading] = useState(true);

  const [error, setError] = useState(null);

  const [searchTerm, setSearchTerm] = useState('');

  const [estadoFiltro, setEstadoFiltro] = useState('');

  const [expandedEmpresas, setExpandedEmpresas] = useState({});



  const [showModal, setShowModal] = useState(false);

  const [cotizacionSeleccionada, setCotizacionSeleccionada] = useState(null);

  const [printFacturaId, setPrintFacturaId] = useState(null);

  const [emailModalCotizacion, setEmailModalCotizacion] = useState(null);

  const [facturaModalCotizacion, setFacturaModalCotizacion] = useState(null);

  const [selectedCotizacionIds, setSelectedCotizacionIds] = useState([]);

  const [combinandoCotizaciones, setCombinandoCotizaciones] = useState(false);



  const cargarDatos = async () => {

    setLoading(true);

    setError(null);

    try {

      // Cargar cotizaciones agrupadas por empresa

      const cotResponse = await cotizacionesService.getPorEmpresa();

      let cotizacionesData = [];

      if (cotResponse.success) {

        cotizacionesData = cotResponse.data || [];

        setCotizacionesPorEmpresa(cotizacionesData);



        // Expandir todas las empresas por defecto

        const expanded = {};

        cotizacionesData.forEach(e => expanded[e.id] = true);

        setExpandedEmpresas(expanded);

      }



      // Cargar estadísticas

      const statsResponse = await cotizacionesService.getStats();

      if (statsResponse.success) {

        setStats(statsResponse.data);

      }






    } catch (error) {

      console.error('Error al cargar datos:', error);

      setError(error.message || 'Error al cargar cotizaciones');

    } finally {

      setLoading(false);

    }

  };



  useEffect(() => { cargarDatos(); }, []);






  // Filtrar cotizaciones

  const empresasFiltradas = cotizacionesPorEmpresa.map(empresa => {

    let cotizacionesFiltradas = empresa.cotizaciones || [];



    if (searchTerm) {

      const term = searchTerm.toLowerCase();

      cotizacionesFiltradas = cotizacionesFiltradas.filter(c =>

        c.numero?.toLowerCase().includes(term) ||

        c.solicitud_nombre?.toLowerCase().includes(term) ||

        empresa.nombre?.toLowerCase().includes(term)

      );

    }



    if (estadoFiltro) {

      cotizacionesFiltradas = cotizacionesFiltradas.filter(c => c.estado === estadoFiltro);

    }



    return { ...empresa, cotizaciones: cotizacionesFiltradas };

  }).filter(e => e.cotizaciones.length > 0);



  const toggleEmpresa = (empresaId) => {

    setExpandedEmpresas(prev => ({ ...prev, [empresaId]: !prev[empresaId] }));

  };

  // ==================== COMBINAR COTIZACIONES EXISTENTES ====================
  // Solo antes de "confirmar" (que valida stock) tiene sentido mover items
  // entre cotizaciones -- una vez confirmada/en espera de pago/pagada, se
  // deja intacta.
  const ESTADOS_COMBINABLES_COTIZACION = ['borrador', 'en_proceso'];

  const puedeSeleccionarseParaCombinar = (cot) => ESTADOS_COMBINABLES_COTIZACION.includes(cot.estado);

  const toggleSeleccionCotizacion = (cot) => {
    if (!puedeSeleccionarseParaCombinar(cot)) return;
    setSelectedCotizacionIds(prev =>
      prev.includes(cot.id) ? prev.filter(id => id !== cot.id) : [...prev, cot.id]
    );
  };

  const todasLasCotizaciones = cotizacionesPorEmpresa.flatMap(e => e.cotizaciones || []);
  const cotizacionesSeleccionadas = todasLasCotizaciones.filter(c => selectedCotizacionIds.includes(c.id));
  const primeraCotSeleccionada = cotizacionesSeleccionadas[0];
  const mismoClienteCotSel = !!(primeraCotSeleccionada?.cliente_id &&
    cotizacionesSeleccionadas.every(c => c.cliente_id === primeraCotSeleccionada.cliente_id));
  const mismaEmpresaCotSel = !!(primeraCotSeleccionada?.empresa_id &&
    cotizacionesSeleccionadas.every(c => c.empresa_id === primeraCotSeleccionada.empresa_id));
  const combinacionCotValida = cotizacionesSeleccionadas.length >= 2 && (mismoClienteCotSel || mismaEmpresaCotSel);

  const handleCombinarCotizaciones = async () => {
    if (!combinacionCotValida || combinandoCotizaciones) return;

    const numeros = cotizacionesSeleccionadas.map(c => c.numero || `#${c.id}`).join(', ');
    const confirmado = await confirmModal(
      'Combinar cotizaciones',
      `¿Combinar ${cotizacionesSeleccionadas.length} cotizaciones (${numeros}) en una sola? Las demás quedarán vacías y se eliminarán.`
    );
    if (!confirmado) return;

    try {
      setCombinandoCotizaciones(true);
      const respuesta = await cotizacionesService.combinar(selectedCotizacionIds);
      if (respuesta.success) {
        await notify('success', 'Cotizaciones combinadas', `Se combinaron en ${respuesta.cotizacion.numero}.`);
        setSelectedCotizacionIds([]);
        await cargarDatos();
      } else {
        await notify('error', 'No se pudo combinar', respuesta.message || 'No se pudo combinar las cotizaciones.');
      }
    } catch (error) {
      console.error('Error al combinar cotizaciones:', error);
      await notify('error', 'Error al combinar', error.message || 'Error al combinar cotizaciones.');
    } finally {
      setCombinandoCotizaciones(false);
    }
  };



  const abrirCotizacion = async (cot) => {

    // Cargar la cotización completa con sus artículos

    try {

      const result = await cotizacionesService.getById(cot.id);

      console.log('Respuesta getById:', result);

      // Manejar diferentes estructuras de respuesta

      let data = result;

      if (result.success !== undefined && result.data) {

        data = result.data;

      } else if (result.data && !result.articulos) {

        // Caso donde los datos están anidados en data.data

        data = result.data.data || result.data;

      }

      

      console.log('Datos procesados:', data);

      console.log('Artículos:', data.articulos);

      console.log('Número de artículos:', data.articulos?.length || 0);

      if (data) {

        // Formatear artículos con valores por defecto

        const cotizacionFormateada = {

          ...data,

          articulos: formatearArticulos(data.articulos || [])

        };

        console.log('Cotización formateada:', cotizacionFormateada);

        // ========== AUTOMÁTICO: Agregar artículos sin stock a Compras Pendientes ==========
        // Detectar artículos sin stock y enviarlos al módulo de Facturas/Compras Pendientes
        const articulosSinStock = cotizacionFormateada.articulos.filter(art => art.tiene_stock === false);

        if (articulosSinStock.length > 0) {
          console.log(`🔍 Detectados ${articulosSinStock.length} artículos sin stock en ${cotizacionFormateada.numero || 'COT-' + cotizacionFormateada.id}`);

          try {
            // Intentar usar backend primero
            await comprasPendientesService.batchDesdeCotizacion(
              cotizacionFormateada.id,
              cotizacionFormateada.numero || `COT-${cotizacionFormateada.id}`,
              cotizacionFormateada.cliente_nombre,
              cotizacionFormateada.empresa_nombre,
              articulosSinStock.map(art => ({
                articulo_id: art.id,
                nombre: art.nombre || art.articulo_nombre,
                cantidad: art.cantidad,
                descripcion: art.descripcion || art.articulo_descripcion,
                via_compra: art.via_compra,
                link_proveedor: art.link_proveedor
              }))
            );
            console.log(`✅ ${articulosSinStock.length} artículo(s) sin stock enviado(s) al backend`);
          } catch (error) {
            console.error('❌ Error al enviar artículos sin stock:', error.message);
          }
        }
        // ========== FIN AUTOMÁTICO ==========

        setCotizacionSeleccionada(cotizacionFormateada);

        setShowModal(true);

      }

    } catch (error) {

      console.error('Error completo:', error);

      await notify('error', 'Error al cargar cotización', error.message || 'No se pudo obtener los datos de la cotización.');

    }

  };



  // Helper para generar HTML de cotización con datos inyectados
  const generarHTMLCotizacionEmail = (cotizacionData) => {
    console.log('Generando HTML para:', cotizacionData.numero);
    let html = cotizacionTemplateHtml;
    
    // Helper para reemplazar de forma segura
    // Busca: id="XXX" ... >CONTENIDO_ACTUAL< (donde ... son otros atributos opcionales)
    const reemplazar = (id, valor) => {
      // Busca id="XXX" seguido de cualquier cosa hasta >, luego contenido hasta <
      const regex = new RegExp(`id="${id}"[^>]*>[^<]*`, 'g');
      const nuevoValor = (valor || '').toString().replace(/</g, '&lt;').replace(/>/g, '&gt;');
      html = html.replace(regex, `id="${id}">${nuevoValor}`);
      console.log(`Reemplazar #${id} = "${valor}" - resultado incluido: ${html.includes(`id="${id}">${nuevoValor}`)}`);
    };
    
    // Formatear moneda (formato español: US$ 1.234,56)
    const formatCurrency = (value) => {
      if (!value || isNaN(value)) return 'US$ 0,00';
      return 'US$ ' + parseFloat(value).toFixed(2).replace(/\./g, ',').replace(/\d(?=(\d{3})+,)/g, '$&.');
    };
    
    // Formatear fecha
    const formatFecha = (fechaStr) => {
      if (!fechaStr) return '';
      const fecha = new Date(fechaStr);
      return fecha.toLocaleDateString('es-VE');
    };
    
    // Datos de la cotización (mapeo de campos del API)
    reemplazar('cot-numero', cotizacionData.numero);  // API usa 'numero', no 'numero_cotizacion'
    reemplazar('cot-fecha', formatFecha(cotizacionData.fecha_creacion));
    reemplazar('cli-nombre', cotizacionData.cliente_nombre);
    reemplazar('cli-rif', cotizacionData.cliente_rif);
    reemplazar('cli-direccion', cotizacionData.cliente_direccion);
    reemplazar('cont-nombre', cotizacionData.cliente_nombre);  // Usar cliente_nombre como contacto
    reemplazar('cont-telefono', cotizacionData.cliente_telefono);
    reemplazar('cont-email', cotizacionData.cliente_email);
    reemplazar('cot-concepto', cotizacionData.solicitud_nombre || 'Suministro');
    reemplazar('foot-pago', 'Transferencia / Contado / Crédito');
    reemplazar('foot-vendedor', cotizacionData.usuario_creador_nombre || '');
    reemplazar('foot-condiciones', 'Sujeto a verificación');
    
    // Procesar artículos (12 filas)
    const articulos = cotizacionData.articulos || [];
    let subtotalGeneral = 0;
    
    for (let i = 0; i < 12; i++) {
      const art = articulos[i];
      const rowNum = i + 1;
      
      if (art) {
        const cantidad = art.cantidad || 1;
        let subtotalItem = parseFloat(art.subtotal || 0);
        if (subtotalItem === 0) {
          const precio = parseFloat(art.precio_venta || art.precio_unitario || 0);
          subtotalItem = precio * cantidad;
        }
        subtotalGeneral += subtotalItem;
        const precioUnitario = subtotalItem / cantidad;
        
        reemplazar(`item-${rowNum}-cant`, cantidad);
        reemplazar(`item-${rowNum}-desc`, art.articulo_nombre || art.nombre || '');
        reemplazar(`item-${rowNum}-precio`, formatCurrency(precioUnitario));
        reemplazar(`item-${rowNum}-total`, formatCurrency(subtotalItem));
      } else {
        reemplazar(`item-${rowNum}-cant`, '');
        reemplazar(`item-${rowNum}-desc`, '');
        reemplazar(`item-${rowNum}-precio`, '');
        reemplazar(`item-${rowNum}-total`, '');
      }
    }
    
    // Usar total del backend (ya incluye IVA) o calcular
    const totalFinal = cotizacionData.total || (subtotalGeneral * 1.16);
    console.log('Total final:', totalFinal);
    reemplazar('cot-total', formatCurrency(totalFinal));
    
    // Reemplazar logo con URL absoluta para que funcione en el PDF
    const logoUrl = window.location.origin + '/Logo%20GAMAN.png';
    html = html.replace(/src="\/Logo GAMAN\.png"/g, `src="${logoUrl}"`);
    console.log('Logo URL:', logoUrl);
    
    // Ocultar barra de acciones (solo para impresión frontend)
    html = html.replace(/id="action-bar"[^>]*>/g, `id="action-bar" style="display:none !important;">`);
    
    // Eliminar scripts (no necesarios en PDF)
    html = html.replace(/<script>[\s\S]*?<\/script>/g, '');
    
    console.log('HTML generado correctamente');
    return html;
  };



  // Manejar envío de cotización por email

  const handleEnviarEmail = async (datosEmail) => {

    try {
      console.log('========================================');
      console.log('ENVIAR EMAIL - Iniciando proceso');
      console.log('Cotizacion ID:', datosEmail.cotizacionId);
      
      // Obtener datos completos de la cotización con artículos
      const apiResponse = await cotizacionesService.getById(datosEmail.cotizacionId);
      console.log('Respuesta API completa:', apiResponse);
      
      // Extraer datos (puede ser apiResponse.data o directamente apiResponse)
      const cotizacionCompleta = apiResponse.success !== undefined ? apiResponse.data : apiResponse;
      console.log('Datos extraidos:', cotizacionCompleta);
      console.log('TODOS los campos:', Object.keys(cotizacionCompleta));
      console.log('Articulos:', cotizacionCompleta.articulos);
      console.log('Numero cotizacion:', cotizacionCompleta.numero_cotizacion);
      console.log('Numero:', cotizacionCompleta.numero);
      console.log('Cliente:', cotizacionCompleta.cliente_nombre);
      console.log('Fecha:', cotizacionCompleta.fecha_creacion, cotizacionCompleta.fecha_cotizacion);
      
      // Generar HTML con datos inyectados
      const htmlCotizacion = generarHTMLCotizacionEmail(cotizacionCompleta);
      console.log('HTML generado:', htmlCotizacion.length, 'caracteres');
      
      // Preparar payload EXACTO para el backend
      const payload = {
        email_destino: datosEmail.email_destino,
        asunto: datosEmail.asunto,
        mensaje: datosEmail.mensaje,
        html_cotizacion: htmlCotizacion,  // ← Campo requerido por backend
        numero_cotizacion: cotizacionCompleta.numero || cotizacionCompleta.numero_cotizacion,  // API usa 'numero'
        cliente_nombre: cotizacionCompleta.cliente_nombre
      };
      
      console.log('PAYLOAD a enviar al backend:', {
        email_destino: payload.email_destino,
        asunto: payload.asunto,
        html_cotizacion: payload.html_cotizacion ? `${payload.html_cotizacion.length} caracteres` : 'VACIO',
        numero_cotizacion: payload.numero_cotizacion,
        cliente_nombre: payload.cliente_nombre
      });
      console.log('========================================');

      const response = await cotizacionesService.enviarPorEmail(
        datosEmail.cotizacionId,
        payload
      );

      if (response.success) {
        await notify('success', '¡Email enviado!', response.message || 'El email con la cotización fue enviado correctamente.');
      }

    } catch (error) {

      await notify('error', 'Error al enviar email', error.message || 'No se pudo enviar el email.');

      throw error;

    }

  };



  // Manejar envío de factura por email

  const handleEnviarFactura = async (datosEmail) => {

    try {

      const response = await cotizacionesService.enviarFacturaPorEmail(

        datosEmail.cotizacionId,

        {

          email_destino: datosEmail.email_destino,

          asunto: datosEmail.asunto,

          mensaje: datosEmail.mensaje

        }

      );

      if (response.success) {
        await notify('success', '¡Factura enviada!', response.message || 'La factura fue enviada correctamente por email.');
      }

    } catch (error) {

      await notify('error', 'Error al enviar factura', error.message || 'No se pudo enviar la factura.');

      throw error;

    }

  };



  // Manejar pasar cotización a facturación

  // Devuelve true si quedó pasada a factura (para que quien la llame -- el
  // modal de cotización -- sepa que ya puede refrescar y abrir el modal de
  // facturación), false si el usuario canceló o falló.
  const handlePasarAFactura = async (cotizacion) => {

    try {

      // Verificar si ya fue pasada a factura
      if (cotizacion.pasada_a_factura === true || cotizacion.pasada_a_factura === 1) {
        await notify('warning', 'Ya procesada', 'Esta cotización ya fue pasada al módulo de Facturación anteriormente.');
        return true;
      }

      const ok = await confirmModal(
        `¿Pasar ${cotizacion.numero} a Facturación?`,
        'Esta acción moverá la cotización al módulo de Facturación con estado "En Espera de Pago". Una vez realizada, no podrá revertirse fácilmente.'
      );
      if (!ok) return false;

      // Llamar al servicio backend para pasar la cotización a facturación
      const result = await cotizacionesService.pasarAFactura(cotizacion.id, {});

      console.log('✅ Cotización pasada a facturación:', result);

      // Sin esto el botón "Pasar a Factura" seguia visible hasta recargar
      // la pagina: pasada_a_factura solo se actualizaba en el backend.
      await cargarDatos();

      await notify('success', '¡Listo!', `La cotización ${cotizacion.numero} fue pasada al módulo de Facturación correctamente.`);

      // Refrescar la lista/árbol raíz -- el modal de cotización (si está
      // abierto) se refresca por su cuenta llamando a recargarCotizacion().
      await cargarDatos();

      return true;

    } catch (error) {

      console.error('❌ Error al pasar cotización a facturación:', error);

      const errorMessage = error.response?.data?.message || error.message || 'Error desconocido';
      await notify('error', 'Error al pasar a Facturación', errorMessage);

      return false;

    }

  };



  return (
    <>
    {ModalRenderer}
    <div className="p-4 lg:p-6 bg-slate-100 min-h-screen">

      {/* Header */}

      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">

        <div>

          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-3">

            <FileText className="text-brand-600" /> Cotizaciones

          </h1>

          <p className="text-slate-600">Vista por empresa • Conectado a la base de datos</p>

        </div>

        <button onClick={cargarDatos} disabled={loading}

          className="px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 flex items-center gap-2 disabled:bg-slate-400">

          <RefreshCw size={18} className={loading ? 'animate-spin' : ''} /> Actualizar

        </button>

      </div>



      {/* Error */}

      {error && (

        <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6 flex items-center gap-3">

          <AlertCircle className="text-red-500 flex-shrink-0" size={24} />

          <div className="flex-1">

            <p className="font-semibold text-red-700">Error al cargar datos</p>

            <p className="text-sm text-red-600">{error}</p>

          </div>

          <button onClick={cargarDatos} className="px-3 py-1 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 text-sm font-medium">

            Reintentar

          </button>

        </div>

      )}



      {/* Stats */}

      {stats && (

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">

          <div className="bg-white rounded-xl p-3 shadow-sm border-l-4 border-brand-500">

            <p className="text-xs text-slate-500">Total</p>

            <p className="text-2xl font-bold text-brand-600">{stats.total || 0}</p>

          </div>

          <div className="bg-white rounded-xl p-3 shadow-sm border-l-4 border-slate-400">

            <p className="text-xs text-slate-500">Borrador</p>

            <p className="text-2xl font-bold text-slate-600">{stats.borrador || 0}</p>

          </div>

          <div className="bg-white rounded-xl p-3 shadow-sm border-l-4 border-yellow-500">

            <p className="text-xs text-slate-500">En Proceso</p>

            <p className="text-2xl font-bold text-yellow-600">{stats.en_proceso || 0}</p>

          </div>

          <div className="bg-white rounded-xl p-3 shadow-sm border-l-4 border-brand-400">

            <p className="text-xs text-slate-500">Listas</p>

            <p className="text-2xl font-bold text-brand-500">{stats.lista_confirmar || 0}</p>

          </div>

          <div className="bg-white rounded-xl p-3 shadow-sm border-l-4 border-green-500">

            <p className="text-xs text-slate-500">Confirmadas</p>

            <p className="text-2xl font-bold text-green-600">{stats.confirmadas || 0}</p>

          </div>

          <div className="bg-white rounded-xl p-3 shadow-sm border-l-4 border-green-600">

            <p className="text-xs text-slate-500">Monto Total</p>

            <p className="text-lg font-bold text-green-700">{formatearMoneda(stats.total_monto || 0)}</p>

          </div>

        </div>

      )}















      {/* Filtros */}

      <div className="bg-white rounded-xl p-4 shadow-sm mb-6">

        <div className="flex flex-col sm:flex-row gap-3">

          <div className="flex-1 relative">

            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />

            <input type="text" placeholder="Buscar por número, solicitud, empresa..."

              value={searchTerm} onChange={e => setSearchTerm(e.target.value)}

              className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500" />

          </div>

          <select value={estadoFiltro} onChange={e => setEstadoFiltro(e.target.value)}

            className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-brand-500 min-w-[180px]">

            <option value="">Todos los estados</option>

            {Object.entries(ESTADOS_CONFIG).map(([k, v]) => (

              <option key={k} value={k}>{v.label}</option>

            ))}

          </select>

          <button onClick={() => { const all = {}; cotizacionesPorEmpresa.forEach(e => all[e.id] = true); setExpandedEmpresas(all); }}

            className="px-4 py-2 border rounded-lg hover:bg-slate-50 text-sm font-medium">

            Expandir todo

          </button>

          <button onClick={() => setExpandedEmpresas({})}

            className="px-4 py-2 border rounded-lg hover:bg-slate-50 text-sm font-medium">

            Colapsar todo

          </button>

        </div>

      </div>



      {/* Barra de acción masiva: combinar cotizaciones seleccionadas */}
      {selectedCotizacionIds.length > 0 && (
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-amber-50 border border-amber-300 rounded-xl p-4 mb-4">
          <div>
            <p className="font-semibold text-amber-800">{selectedCotizacionIds.length} cotización(es) seleccionada(s)</p>
            <p className="text-sm text-amber-700">
              Deben pertenecer todas al mismo cliente o a la misma empresa, y estar en borrador o en proceso, para combinarse.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setSelectedCotizacionIds([])}
              className="px-3 py-2 text-sm rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50"
            >
              Cancelar selección
            </button>
            <button
              onClick={handleCombinarCotizaciones}
              disabled={!combinacionCotValida || combinandoCotizaciones}
              className="px-4 py-2 text-sm rounded-lg bg-amber-600 text-white font-medium hover:bg-amber-700 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {combinandoCotizaciones ? <Loader2 size={16} className="animate-spin" /> : null}
              Combinar en una cotización
            </button>
          </div>
        </div>
      )}

      {/* Árbol de Empresas */}

      {loading ? (

        <div className="flex items-center justify-center py-16">

          <Loader2 size={40} className="animate-spin text-brand-600" />

          <span className="ml-3 text-slate-600">Cargando cotizaciones...</span>

        </div>

      ) : empresasFiltradas.length === 0 ? (

        <div className="text-center py-16 bg-white rounded-xl">

          <FileText size={64} className="mx-auto text-slate-300 mb-4" />

          <p className="text-slate-500 text-lg">No se encontraron cotizaciones</p>

          <p className="text-slate-400 text-sm mt-1">

'Crea una solicitud primero para generar cotizaciones'
          </p>

        </div>

      ) : (

        <div>

          {empresasFiltradas.map((empresa) => (

            <EmpresaTreeItem

              key={empresa.id}

              empresa={empresa}

              cotizaciones={empresa.cotizaciones}

              expanded={expandedEmpresas[empresa.id]}

              onToggle={() => toggleEmpresa(empresa.id)}

              onSelectCotizacion={abrirCotizacion}

              onPrint={(cot) => setPrintFacturaId(cot.id)}

              onEnviarEmail={(cot) => setEmailModalCotizacion(cot)}

              onEnviarFactura={(cot) => setFacturaModalCotizacion(cot)}

              onPasarAFactura={handlePasarAFactura}

              selectedCotizacionIds={selectedCotizacionIds}

              onToggleSeleccion={toggleSeleccionCotizacion}

              puedeSeleccionarseParaCombinar={puedeSeleccionarseParaCombinar}

            />

          ))}

        </div>

      )}



      {/* Modal */}

      {showModal && cotizacionSeleccionada && (

        <CotizacionModal

          cotizacion={cotizacionSeleccionada}

          onClose={() => { setShowModal(false); setCotizacionSeleccionada(null); }}

          onRefresh={cargarDatos}

          onPrint={(cot) => setPrintFacturaId(cot.id)}

          onEnviarEmail={(cot) => setEmailModalCotizacion(cot)}

          onEnviarFactura={(cot) => setFacturaModalCotizacion(cot)}

          onPasarAFactura={handlePasarAFactura}

        />

      )}



      {/* Modal Envío Cotización Email */}

      {emailModalCotizacion && (

        <EmailSendModal

          cotizacion={emailModalCotizacion}

          onClose={() => setEmailModalCotizacion(null)}

          onEnviar={handleEnviarEmail}

        />

      )}



      {/* Modal Envío Factura Email */}

      {facturaModalCotizacion && (

        <FacturaEmailModal

          cotizacion={facturaModalCotizacion}

          onClose={() => setFacturaModalCotizacion(null)}

          onEnviar={handleEnviarFactura}

        />

      )}



      {/* Modal Impresión / Descarga */}

      {printFacturaId && (

        <FacturaPrintModal

          cotizacionId={printFacturaId}

          onClose={() => setPrintFacturaId(null)}

        />

      )}

    </div>
    </>
  );

};



export { cotizacionesService, comprasPendientesService };
export default CotizacionesView;

